(ns ui.editor2.actions
  (:require [cljs.reader :as edn]
            [clojure.string :as str]
            [datascript.core :as ds]
            [parts.replicant.command :as command]
            [parts.replicant.query :as query]
            [storrito.image :as image]
            [storrito.editor.sticker-design :as sticker-designs]
            [storrito.instagram.story.editor.model :as editor.model]
            [app.util.api-request :as api]
            [storrito.matomo.core :as matomo]
            [ui.editor2.common :refer [transact] :as common]
            [ui.editor2.composition :as composition]
            [ui.editor2.extension-points :as extension-points]
            [ui.editor2.local-assets :as local-assets]
            [ui.editor2.media-limits :as media-limits]
            [ui.editor2.schema :as schema]
            [replicant.dom :as rep]
            ["prosemirror-commands" :as prosemirror-commands]
            ["prosemirror-state" :as prosemirror-state]
            [ui.editor2.editor1-import :as editor1-import]
            [ui.editor2.audio-player :as audio-player]
            [sv.core.async :refer-macros [<?]]
            [ui.editor2.text :as text]
            [ui.upload-manager :as upload-manager]
            [goog.functions :as functions]
            [ui.editor2.timeline :as timeline]
            [cljs.core.async :refer [<!]]
            [cljs.core.async.interop :refer-macros [<p!]]
            [ui.editor2.sticker :as sticker]
            [ui.editor2.recent :as editor2.recent]
            [ui.editor2.defaults :as defaults])
  (:require-macros [cljs.core.async.macros :refer [go]]))

(def min-clip-duration
  100)

(defn pixel-to-ms
  [pixel-per-second pixel]
  (long (* (/ pixel
              pixel-per-second)
           1000)))

(defn transact-asset
  [{:keys [state action]}]
  (transact state (second action)))

(defn clip-duration
  [clip]
  (- (:clip/end clip)
     (:clip/start clip)))

(defn clip-drag-params
  [state evt]
  (let [{:keys [db pixel-per-second drag]} (:editor2 state)
        {:keys [clip/uuid start-x start-offset]} drag
        current-x (.-clientX evt)
        delta-x (- current-x start-x)
        new-left (+ start-offset delta-x)
        clip (ds/entity db
                        [:clip/uuid uuid])
        duration (clip-duration clip)
        new-start (max (pixel-to-ms pixel-per-second
                                    new-left)
                       0)
        new-end (+ new-start
                   duration)]
    {:clip clip
     :new-start new-start
     :new-end new-end}))

(defn snap
  [{:keys [state current-entity new-position]}]
  (let [{:keys [pixel-per-second db]} (:editor2 state)
        eid (:db/id current-entity)
        clips (remove #(= (:db/id %) eid)
                      (common/get-all-clips state))
        slides (remove #(= (:db/id %) eid)
                       (common/slide-clips db))
        snap-positions (concat (mapcat (fn [c]
                                         [(:clip/start c)
                                          (:clip/end c)])
                                       clips)
                               (mapcat (fn [c]
                                         [(:slide/start c)
                                          (:slide/end c)])
                                       slides))
        nearest-snap-position (first (sort-by
                                       (fn [p]
                                         (Math/abs (- new-position p)))
                                       snap-positions))
        distance (Math/abs (- new-position
                              nearest-snap-position))]
    (when (< (* (/ distance 1000)
                pixel-per-second)
             (:timeline/snapping-px defaults/defaults))
      nearest-snap-position)))

(defn snap-clip
  [state {:keys [clip new-start new-end]}]
  (let [new-start-snapped (snap {:state state :current-entity clip :new-position new-start})
        new-end-snapped (snap {:state state :current-entity clip :new-position new-end})]
    (transact state
              [(merge {:clip/uuid (:clip/uuid clip)}
                      (cond (and new-start-snapped
                                 new-end-snapped)
                            (if (< (Math/abs (- new-start new-start-snapped))
                                   (Math/abs (- new-end new-end-snapped)))
                              {:clip/start new-start-snapped
                               :clip/end (+ new-start-snapped
                                            (clip-duration clip))}
                              {:clip/start (max (- new-end-snapped
                                                   (clip-duration clip))
                                                0)
                               :clip/end new-end-snapped})

                            new-start-snapped
                            {:clip/start new-start-snapped
                             :clip/end (+ new-start-snapped
                                          (clip-duration clip))}

                            new-end-snapped
                            {:clip/start (max (- new-end-snapped
                                                 (clip-duration clip))
                                              0)
                             :clip/end new-end-snapped}

                            :else
                            {:clip/start new-start
                             :clip/end new-end}))])))

(defn clip-middle-drag
  [state evt]
  ;; update the clip's position
  (let [{:keys [clip new-start new-end]} (clip-drag-params state
                                                           evt)]
    (snap-clip state
               {:clip clip
                :new-start new-start
                :new-end new-end})))

(defn gif-clip?
  [clip]
  (= :gif (:asset/type (:clip/asset clip))))

(defn max-clip-duration
  [clip]
  (if (gif-clip? clip)
    ##Inf
    (or (:asset/duration (:clip/asset clip))
        ##Inf)))

(defn clamp
  [{:keys [minimum maximum value]}]
  (-> value
      (max minimum)
      (min maximum)))

(defn clip-left-drag
  [state evt]
  (let [{:keys [clip new-start]} (clip-drag-params state
                                                   evt)
        left-border (if (and (:asset/duration (:clip/asset clip))
                             (not (gif-clip? clip)))
                      (- (:clip/start clip)
                         (:clip/offset clip))
                      ##-Inf)
        right-border (- (:clip/end clip)
                        min-clip-duration)
        ;; Force `new-start` into left and right borders:
        new-start-snapped (snap {:state state
                                 :current-entity clip
                                 :new-position new-start})
        new-start-clamped (clamp {:minimum left-border
                                  :maximum right-border
                                  :value (or new-start-snapped
                                             new-start)})]
    (-> state
        (assoc-in [:editor2 :playhead-position]
                  new-start-clamped)
        (transact [(merge {:clip/uuid (:clip/uuid clip)
                           :clip/start new-start-clamped}
                          ;; Transact a new offset only when the asset got a
                          ;; duration (like videos, audios etc) but not for
                          ;; gifs which can loop freely
                          (when (and (:asset/duration (:clip/asset clip))
                                     (not (gif-clip? clip)))
                            {:clip/offset (- new-start-clamped left-border)}
                            ))]))))

(defn clip-right-drag
  [state evt]
  (let [{:keys [db pixel-per-second drag]} (:editor2 state)
        {:keys [clip/uuid start-x start-offset]} drag
        current-x (.-clientX evt)
        delta-x (- current-x start-x)
        clip (ds/entity db
                        [:clip/uuid uuid])
        new-right (+ start-offset delta-x)
        new-end (max (pixel-to-ms pixel-per-second
                                  new-right)
                     0)
        video-start (- (:clip/start clip 0)
                       (:clip/offset clip 0))
        left-border (+ video-start
                       min-clip-duration)
        right-border (+ video-start
                        (max-clip-duration clip))
        new-end-snapped (snap {:state state
                               :current-entity clip
                               :new-position new-end})
        ;; Force `new-end` into left and right borders:
        new-end-clamped (clamp {:minimum left-border
                                :maximum right-border
                                :value (or new-end-snapped
                                           new-end)})]
    (-> state
        (assoc-in [:editor2 :playhead-position]
                  new-end-clamped)
        (transact [{:clip/uuid uuid
                    :clip/end new-end-clamped}]))))

(defn playhead-on-mouse-move
  [state evt]
  (let [{:keys [pixel-per-second drag]} (:editor2 state)
        {:keys [start-x start-offset]} drag
        current-x (.-clientX evt)
        delta-x (- current-x start-x)
        new-left (+ start-offset delta-x)
        new-position (max (pixel-to-ms pixel-per-second
                                       new-left)
                          0)]
    ;; update the playhead's position
    (assoc-in state
              [:editor2 :playhead-position]
              new-position)
    ))

(defn manage-selected-clip
  "Drops any selected clips that are no longer visible at the current
   `:playhead-position`. If the anchor (`:selected-clip`) is dropped but other
   selected clips remain, picks one of them as the new anchor."
  [state]
  (let [{:keys [db playhead-position]} (:editor2 state)
        uuids (common/selected-clip-uuids state)]
    (if (empty? uuids)
      state
      (let [visible-uuids (into #{}
                                (filter (fn [uuid]
                                          (let [clip (ds/entity db [:clip/uuid uuid])]
                                            (and clip
                                                 (<= (:clip/start clip)
                                                     playhead-position
                                                     (:clip/end clip))))))
                                uuids)
            current-anchor (get-in state [:editor2 :selected-clip])
            new-anchor (cond
                         (contains? visible-uuids current-anchor) current-anchor
                         (seq visible-uuids) (first visible-uuids)
                         :else nil)]
        (-> state
            (assoc-in [:editor2 :selected-clips] visible-uuids)
            (assoc-in [:editor2 :selected-clip] new-anchor)
            (cond-> (nil? new-anchor) (update :editor2 dissoc :sidebar-panel)))))))

(defn slide-duration-drag
  [state evt]
  (let [{:keys [pixel-per-second drag db]} (:editor2 state)
        {:keys [slide/uuid start-x old-duration]} drag
        slide (ds/entity db [:slide/uuid uuid])
        current-x (.-clientX evt)
        delta-x (- current-x start-x)
        delta-duration (pixel-to-ms pixel-per-second
                                    delta-x)
        new-duration (+ old-duration delta-duration)
        snapped-duration (snap {:state state
                                :current-entity slide
                                :new-position new-duration})
        clamped-duration (clamp {:value (or snapped-duration
                                            new-duration)
                                 :minimum (:slide/min-duration defaults/defaults)
                                 :maximum (:slide/max-duration defaults/defaults)})]
    (transact state
              [{:slide/uuid uuid
                :slide/duration clamped-duration}])))

(defn handle-mouse-move
  [state evt]
  (if-let [drag (get-in state
                        [:editor2 :drag :drag])]
    (case drag

      :clip-middle
      (clip-middle-drag state
                        evt)

      :clip-left
      (clip-left-drag state
                      evt)

      :clip-right
      (clip-right-drag state
                       evt)

      :playhead
      (playhead-on-mouse-move state
                              evt)

      :slide-duration
      (slide-duration-drag state
                           evt)
      ;; else:
      state
      )
    state))

(defn get-asset-time
  [state clip-uuid]
  (let [{:keys [db playhead-position]} (:editor2 state)
        clip (ds/entity db
                        [:clip/uuid clip-uuid])
        offset (or (:clip/offset clip)
                   0)
        time-ms (-> (+ (- (or playhead-position 0)
                          (or (:clip/start clip) 0))
                       offset)
                    (max offset)
                    (min (+ (:clip/end clip)
                            offset)))
        asset-duration (:asset/duration (:clip/asset clip))]
    (if (and (:clip/loop clip)
             asset-duration)
      (mod time-ms
           asset-duration)
      time-ms)))

(defn media-elements
  "The DOM elements that play the clip: the <video> (or the player of
   a template clip) and, for a clip with `:clip/captions`, the caption
   overlay (see slices/captions), which is driven the same way.

   A <hyperframes-player> is a plain element without `play` / `pause`
   until its script has defined the web component (see
   `ui.hyperframes-templates.player/ensure-script!`); such an element
   is left out, it neither plays nor needs pausing (STORRITO-V2-KH)."
  [clip]
  (let [clip-uuid (:clip/uuid clip)]
    (keep (fn [id]
            (when-let [element (js/document.getElementById id)]
              (when (fn? (.-play element))
                element)))
          (cond-> [(str (name (:asset/type (:clip/asset clip)))
                        "-"
                        clip-uuid)]
            (:clip/captions clip) (conj (str "captions-" clip-uuid))))))

(defn seek-video!
  [{:keys [state clip]}]
  (let [time-ms (get-asset-time state
                                (:clip/uuid clip))
        time-seconds (/ time-ms
                        1000.0)]
    (when (js/isFinite time-seconds)
      (doseq [element (media-elements clip)]
        (set! (.-currentTime element)
              time-seconds)))))

(def seek-video-debounced!
  (functions/debounce seek-video!
                      100))

(defn seek-audio!
  [{:keys [state clip]}]
  (when-let [howl (get-in state
                          [:editor2
                           :audio-players
                           (:clip/uuid clip)])]
    (let [time-ms (get-asset-time state
                                  (:clip/uuid clip))
          time-seconds (/ time-ms
                          1000.0)]
      (when (js/isFinite time-seconds)
        (.seek howl
               time-seconds)))))

(def seek-audio-debounced!
  (functions/debounce seek-audio!
                      50))

(defn seek!
  "Seeks all assets to the corresponding time according to the asset's
   offset start-time and the playhead-position."
  [state]
  (doseq [clip (common/get-all-clips state)]
    (let [asset-type (:asset/type (:clip/asset clip))]
      (case asset-type
        (:video :hyperframes)
        (seek-video-debounced! {:state state
                                :clip clip})

        :audio
        (seek-audio-debounced! {:state state
                                :clip clip})

        ;; else:
        nil
        ))))

(defn on-mouse-move [store evt]
  (let [state (swap! store
                     (fn [state]
                       (manage-selected-clip
                         (handle-mouse-move state
                                            evt))))]
    (when (get #{:clip-middle
                 :clip-left
                 :clip-right
                 :playhead}
               (get-in state
                       [:editor2 :drag :drag]))
      (seek! state))))

(defn q-empty-tracks
  [db]
  (ds/q
    '[:find [?track-uuid ...]
      :where
      [?track :track/uuid ?track-uuid]
      (not [?clip :clip/track ?track])]
    db))

(defn remove-empty-tracks
  [state]
  (let [db (get-in state
                   [:editor2
                    :db])
        empty-tracks (set (q-empty-tracks db))
        timeline (ds/entity db
                            [:timeline/uuid
                             (common/q-timeline-uuid db)])
        tracks (filterv
                 (fn [track-uuid]
                   (not (empty-tracks track-uuid)))
                 (:timeline/tracks timeline))
        tx (cons
             {:timeline/uuid (:timeline/uuid timeline)
              :timeline/tracks tracks}
             (map (fn [track-uuid]
                    [:db/retractEntity [:track/uuid track-uuid]])
                  empty-tracks))]
    (transact state
              tx)))

(defn sort-tracks
  [state]
  (let [db (get-in state
                   [:editor2
                    :db])
        timeline (ds/entity db
                            [:timeline/uuid
                             (common/q-timeline-uuid db)])
        tracks (into [] (sort-by
                          (fn [track-uuid]
                            (let [track (ds/entity db
                                                   [:track/uuid track-uuid])
                                  assets (map :clip/asset (:clip/_track track))]
                              (cond (some #(common/interactive-stickers (:asset/type %))
                                          assets) 0
                                    (some #(common/static-stickers (:asset/type %))
                                          assets) 1
                                    :else 2)))
                          (:timeline/tracks timeline)))
        tx [{:timeline/uuid (:timeline/uuid timeline)
             :timeline/tracks tracks}]]
    (transact state
              tx)))

(defn drag-start
  [state params]
  (update state
          :editor2
          (fn [editor2]
            (assoc editor2
              :drag
              (merge {:start-x (.-clientX (:replicant/js-event params))}
                     (second (:action params)))))))

(defn snap-stickers-to-slides
  [state]
  (let [{:keys [db]} (:editor2 state)
        slide-clips (common/slide-clips db)
        sticker-clips (filter
                        (fn [clip]
                          (common/is-sticker? (:clip/asset clip)))
                        (common/get-all-clips state))
        tx (keep
             (fn [sticker-clip]
               (let [new-start (first (sort-by (fn [slide-start]
                                                 (Math/abs (- (:clip/start sticker-clip)
                                                              slide-start)))
                                               (remove #(= % (:clip/end sticker-clip))
                                                       (map :slide/start slide-clips))))
                     new-end (first (sort-by (fn [slide-start]
                                               (Math/abs (- (:clip/end sticker-clip)
                                                            slide-start)))
                                             (remove #(= % new-start)
                                                     (map :slide/end slide-clips))))]
                 (when (and new-start new-end)
                   {:clip/uuid (:clip/uuid sticker-clip)
                    :clip/end new-end
                    :clip/start new-start})))
             sticker-clips)]
    (transact state
              tx)))

(defn drag-start-handler
  [{:keys [store] :as params}]
  (let [mousemove (partial on-mouse-move
                           store)]
    (.addEventListener js/document
                       "mousemove"
                       mousemove)
    (.addEventListener js/document
                       "mouseup"
                       (fn self [_evt]
                         (.removeEventListener js/document
                                               "mousemove"
                                               mousemove)
                         (.removeEventListener js/document
                                               "mouseup"
                                               self)
                         (swap! store
                                (fn [state]
                                  (-> state
                                      (update :editor2 dissoc :drag)
                                      (snap-stickers-to-slides)
                                      (remove-empty-tracks)
                                      (sort-tracks))))))
    (swap! store
           drag-start
           params)))

(defn clip-move-to-track
  "Moves the clip that is currently dragged (`:clip-middle` drag) onto the
   track of the action.

   The track row's `:mouseover` handler is rendered while the drag is active
   and stays in the DOM until the next re-render. So it can still fire after
   the document `mouseup` already removed `:drag` from the state. Without a
   dragged clip this is a no-op instead of a DataScript transaction with a
   nil `:clip/uuid` (STORRITO-V2-HR)."
  [{:keys [state action]}]
  (let [drag (get-in state [:editor2 :drag])
        clip-uuid (:clip/uuid drag)]
    (if (and (= (:drag drag) :clip-middle)
             clip-uuid)
      (transact state
                [{:clip/uuid clip-uuid
                  :clip/track [:track/uuid (second action)]}])
      state)))

(defn move-track-in-layer-order
  "Move a track up or down in the timeline track order.
   direction should be :up or :down.
   Maintains constraint that sticker tracks stay on top of non-sticker tracks."
  [state direction]
  (if-let [selected-clip (common/get-selected-clip state)]
    (let [db (get-in state [:editor2 :db])
          current-track-uuid (:track/uuid (:clip/track selected-clip))
          timeline (ds/entity db
                              [:timeline/uuid (common/q-timeline-uuid db)])
          tracks (:timeline/tracks timeline)
          current-idx (.indexOf tracks current-track-uuid)
          target-idx (if (= direction :up)
                       (dec current-idx)
                       (inc current-idx))]
      (if (and (>= current-idx 0)
               (>= target-idx 0)
               (< target-idx (count tracks)))
        (let [current-is-sticker (common/track-contains-stickers? db
                                                                   current-track-uuid)
              target-track-uuid (nth tracks target-idx)
              target-is-sticker (common/track-contains-stickers? db
                                                                  target-track-uuid)]
          (if (= current-is-sticker target-is-sticker)
            (let [new-tracks (vec
                               (assoc tracks
                                 current-idx target-track-uuid
                                 target-idx current-track-uuid))]
              (transact state
                        [{:timeline/uuid (:timeline/uuid timeline)
                          :timeline/tracks new-tracks}]))
            state))
        state))
    state))

(defn move-clip-layer-up
  [state]
  (move-track-in-layer-order state :up))

(defn move-clip-layer-down
  [state]
  (move-track-in-layer-order state :down))

(defn add-track
  [state action new-track-uuid]
  (let [db (get-in state
                   [:editor2
                    :db])
        timeline (ds/entity db
                            [:timeline/uuid
                             (common/q-timeline-uuid db)])
        tracks (:timeline/tracks timeline)
        after-track (:after-track (second action))
        new-tracks (vec
                     (if (= after-track
                            :first)
                       (cons new-track-uuid
                             tracks)
                       (mapcat
                         (fn [track-uuid]
                           (if (= track-uuid
                                  after-track)
                             [track-uuid
                              new-track-uuid]
                             [track-uuid]))
                         tracks)))]
    (transact state
              [{:clip/uuid (:clip/uuid (second action))
                :clip/track "new-track"}
               {:db/id "new-track"
                :track/uuid new-track-uuid}
               {:timeline/uuid (:timeline/uuid
                                 timeline)
                :timeline/tracks new-tracks}])))

(defn new-track
  [{:keys [state action]}]
  (if (:clip/uuid (second action))
    (let [new-track-uuid (random-uuid)]
      (-> state
          (add-track action
                     new-track-uuid)
          (remove-empty-tracks)
          (sort-tracks)))
    state))

(defn cycle-overlapping-clips
  "Cycles through clips that overlap at the current clip's position.
   Useful for selecting clips that are visually covered by other clips."
  [state clip-uuid]
  (let [db (get-in state [:editor2 :db])
        current-clip (ds/entity db [:clip/uuid clip-uuid])]
    (if current-clip
      (let [track-uuid (:track/uuid (:clip/track current-clip))
            ;; Use the middle of the clip as the reference position
            position-ms (+ (:clip/start current-clip)
                          (/ (- (:clip/end current-clip)
                                (:clip/start current-clip))
                             2))
            overlapping (common/find-overlapping-clips-at-position
                          db
                          track-uuid
                          position-ms)
            current-idx (.indexOf (mapv :clip/uuid overlapping)
                                  clip-uuid)
            next-idx (if (>= current-idx 0)
                       (mod (inc current-idx) (count overlapping))
                       0)
            next-clip (when (pos? (count overlapping))
                        (nth overlapping next-idx))]
        (if next-clip
          (assoc-in state [:editor2 :selected-clip] (:clip/uuid next-clip))
          state))
      state)))

(defn delete-clip
  [state]
  (let [uuids (common/selected-clip-uuids state)]
    (if (seq uuids)
      (-> state
          (transact (mapv (fn [uuid] [:db/retractEntity [:clip/uuid uuid]])
                          uuids))
          (update :editor2 (fn [e]
                             (-> e
                                 (dissoc :selected-clip)
                                 (assoc :selected-clips #{})))))
      state)))

(defn cut-clip
  [state]
  (let [editor2 (:editor2 state)]
    (if-let [selected (:selected-clip editor2)]
      (let [clip (ds/entity (:db editor2)
                            [:clip/uuid selected])
            {:keys [clip/start clip/end]} clip
            playhead-position (:playhead-position editor2)]
        (if (< start
               playhead-position
               end)
          (transact state
                    [(merge
                       (select-keys clip
                                    [:clip/name
                                     :clip/transform
                                     :clip/end
                                     :clip/captions])
                       (when (or (#{:video :audio} (get-in clip
                                                           [:clip/asset
                                                            :asset/type]))
                                 (:clip/offset clip))
                         {:clip/offset (+ (:clip/offset clip
                                            0)
                                          (- playhead-position
                                             (:clip/start clip)))})
                       {:clip/uuid (random-uuid)
                        :clip/start playhead-position
                        :clip/asset (:db/id (:clip/asset clip))
                        :clip/track (:db/id (:clip/track clip))})
                     {:clip/uuid (:clip/uuid clip)
                      :clip/end (dec playhead-position)}])
          state))
      state)))

(defn zoom-in
  [state]
  (update-in
    state
    [:editor2 :pixel-per-second]
    (fn [pixel-per-second]
      (min (+ pixel-per-second
              10)
           (:timeline/max-zoom defaults/defaults)))))

(defn zoom-out
  [state]
  (update-in
    state
    [:editor2 :pixel-per-second]
    (fn [pixel-per-second]
      (max (- pixel-per-second
              10)
           (:timeline/min-zoom defaults/defaults)))))

(defn composition-zoom-out
  [state]
  (update-in
    state
    [:editor2 :composition-zoom]
    (fn [composition-zoom]
      (min (+ composition-zoom
              (:composition/zoom-step defaults/defaults))
           (:composition/max-zoom defaults/defaults)))))

(defn composition-zoom-in
  [state]
  (update-in
    state
    [:editor2 :composition-zoom]
    (fn [composition-zoom]
      (max (- composition-zoom
              (:composition/zoom-step defaults/defaults))
           (:composition/min-zoom defaults/defaults)))))

(defn clamp-min-scale
  "Clamps a scale gesture to the clip's minimal scale (see
   `common/min-clip-scale`). When the limit is hit, the previous
   translate is kept as well, so the clip does not drift while the
   gesture tries to shrink it further."
  [new-transform old-transform clip]
  (let [[sx _sy sz] (:scale new-transform)
        min-scale (common/min-clip-scale clip)]
    (if (and sx
             (< sx min-scale))
      (assoc new-transform
             :scale [min-scale min-scale (or sz 1)]
             :translate (or (:translate old-transform)
                            [0.0 0.0]))
      new-transform)))

(defn transform-clip
  "Merge a partial `transform` map (any subset of `:translate`, `:rotate`,
   `:scale`) into the clip's existing `:clip/transform`. Per-event
   handlers (drag/rotate/scale) dispatch only the changed component, so
   merging is essential — a plain replace would clobber the other two."
  [{:keys [state action]}]
  (let [[_ transform selected-uuid] action
        db (get-in state [:editor2 :db])
        clip (ds/entity db [:clip/uuid selected-uuid])
        old-transform (or (:clip/transform clip) {})
        new-transform (cond-> (merge old-transform transform)
                        (:scale transform)
                        (clamp-min-scale old-transform clip))]
    (transact state
              [{:clip/uuid selected-uuid
                :clip/transform new-transform}])))

(def max-recent-giphy-entries
  1000)

(defn giphy-entry->sidebar-state
  [entry]
  {:mode (keyword (or (:mode entry)
                      "stickers"))
   :search (or (:search entry)
               "trending?")
   :search-value (or (:searchValue entry)
                     "")
   :search-offset (or (:searchOffset entry)
                      0)
   :selected-giphy-id (:id entry)})

(defn read-giphy-sidebar-state
  [asset-uuid]
  (when asset-uuid
    (try
      (when-let [entry (some (fn [entry]
                               (when (= (str asset-uuid)
                                        (:assetUuid entry))
                                 entry))
                             (:giphy (editor2.recent/read-editor2-recent)))]
        (giphy-entry->sidebar-state entry))
      (catch :default e
        (js/console.warn "Failed to read Giphy sidebar state from localStorage" e)
        nil))))

(defn giphy-entry
  [asset-uuid giphy-state]
  {:assetUuid (str asset-uuid)
   :id (:id giphy-state)
   :mode (some-> (:mode giphy-state)
                 name)
   :search (or (:search giphy-state)
               "trending?")
   :searchValue (or (:search-value giphy-state)
                    "")
   :searchOffset (or (:search-offset giphy-state)
                     0)})

(defn write-giphy-sidebar-state!
  [asset-uuid giphy-state]
  (try
    (when (and asset-uuid
               giphy-state)
      (let [entry (giphy-entry asset-uuid
                               giphy-state)]
        (editor2.recent/write-editor2-recent!
          (update (editor2.recent/read-editor2-recent)
                  :giphy
                  editor2.recent/put-latest-entry
                  entry
                  :assetUuid
                  max-recent-giphy-entries))))
    (catch :default e
      (js/console.warn "Failed to write Giphy sidebar state to localStorage" e))))

(defn giphy-asset?
  [asset]
  (and (#{:gif :video} (:asset/type asset))
       (or (str/starts-with? (or (:asset/name asset)
                                 "")
                             "giphy-")
           (str/includes? (or (:asset/uri asset)
                              "")
                          "giphy.com")
           (some? (read-giphy-sidebar-state (:asset/uuid asset))))))

(defn giphy-sidebar-state
  [asset]
  (read-giphy-sidebar-state (:asset/uuid asset)))

(defn set-sidebar
  [state clip-uuid]
  (let [db (get-in state
                   [:editor2
                    :db])
        asset-db-id (get-in
                      (ds/entity db [:clip/uuid clip-uuid])
                      [:clip/asset :db/id])
        asset (ds/entity db asset-db-id)
        asset-type (:asset/type asset)
        giphy? (giphy-asset? asset)
        sidebar-panel (if giphy?
                        :gif-sidebar
                        asset-type)]
    (cond-> (update state :editor2
                    assoc
                    :sidebar-panel sidebar-panel
                    ;; When opening the sidebar, we need to init the text editor.
                    ;; Also, when switching from one text asset to another.
                    :init-text-editor? (= asset-type
                                          :text))
      giphy?
      (assoc-in [:editor2 :giphy-sidebar]
                {:clip-uuid clip-uuid
                 :panel-key clip-uuid
                 :initial-state (giphy-sidebar-state asset)})

      (not giphy?)
      (update :editor2 dissoc :giphy-sidebar))))

(defn select-clip
  [state clip-uuid]
  (let [db (get-in state
                   [:editor2
                    :db])
        clip (ds/entity db [:clip/uuid clip-uuid])]
    (-> state
        (assoc-in [:editor2
                   :selected-clip]
                  clip-uuid)
        (assoc-in [:editor2
                   :selected-clips]
                  #{clip-uuid})
        (set-sidebar clip-uuid)
        (update-in [:editor2
                    :playhead-position]
                   (fn [playhead-position]
                     (if (<= (:clip/start clip)
                             playhead-position
                             (:clip/end clip))
                       playhead-position
                       (:clip/start clip)))))))

(defn toggle-clip-selection
  "Adds or removes `clip-uuid` from the multi-selection set. The toggled UUID
   becomes the anchor (`:selected-clip`) when added; when removed, the anchor
   falls back to any remaining selected UUID, or nil if the set is empty."
  [state clip-uuid]
  (let [current (common/selected-clip-uuids state)
        next (if (contains? current clip-uuid)
               (disj current clip-uuid)
               (conj current clip-uuid))
        anchor (cond
                 (contains? next clip-uuid) clip-uuid
                 (seq next) (first next)
                 :else nil)]
    (-> state
        (assoc-in [:editor2 :selected-clips] next)
        (assoc-in [:editor2 :selected-clip] anchor)
        (cond-> anchor (set-sidebar anchor)
                (nil? anchor) (update :editor2 dissoc :sidebar-panel)))))

(defn select-multiple-clips
  "Replaces the canvas selection with the given UUIDs. Used by the marquee."
  [state clip-uuids]
  (let [uuids (set clip-uuids)
        anchor (first uuids)]
    (-> state
        (assoc-in [:editor2 :selected-clips] uuids)
        (assoc-in [:editor2 :selected-clip] anchor)
        (cond-> anchor (set-sidebar anchor)
                (nil? anchor) (update :editor2 dissoc :sidebar-panel)))))

(defn extend-selection-with-clips
  "Adds the given UUIDs to the existing selection. Used by shift+marquee."
  [state clip-uuids]
  (select-multiple-clips state
                         (into (common/selected-clip-uuids state)
                               clip-uuids)))

(defn sync-moveable-to-selection!
  "Imperatively sync the Moveable.js instance to the current selection in
   `state` (single, multi, or none)."
  [state]
  (let [uuids (common/selected-clip-uuids state)]
    (cond
      (empty? uuids)
      (composition/clear-movable-selection!)

      (= 1 (count uuids))
      (composition/select-movable-layer-uuid (first uuids))

      :else
      (composition/select-movable-layer-group uuids))))

(defn focus-editor-container!
  "Moves the keyboard focus to the editor container so that the keyboard
   shortcuts act on the new selection. Does nothing when the container is not
   in the DOM, e.g. when a sticker modal's callback runs after the editor has
   been unmounted."
  []
  (when-let [container (js/document.getElementById composition/editor-container-id)]
    (.focus container)))

(defn select-movable-layer!
  [clip-uuid]
  (composition/select-movable-layer-uuid clip-uuid)
  (focus-editor-container!))

(defn select-clip!
  "Selects the clip with the `:clip/uuid` provided as the first argument of the
   action. Moves the `:playhead-position` to the start of the clip, if it is not
   within the clip's start and end. "
  [{:keys [store action]}]
  (let [[_ clip-uuid] action]
    (swap! store
           select-clip
           clip-uuid)
    ;; It is important to select the movable layer after the
    ;; state has changed via swap!. Otherwise, there will be no
    ;; movable object that can be selected.
    (select-movable-layer! clip-uuid)
    ))

(defonce ^:private last-click-clip
  ;; The design panel's `:mousedown` and Moveable's `click` callback both fire
  ;; for the same clip click (mousedown first, click after mouseup). Without
  ;; coordination, a shift-click would toggle twice and net zero change. Track
  ;; the last handled UUID so the second fire is a no-op.
  (atom nil))

(def ^:private click-dedupe-ms 150)
(def ^:private double-click-ms 500)

(defn- native-double-click?
  [js-event]
  (<= 2 (or (some-> js-event .-detail)
            0)))

(defn- open-sticker-edit-modal-action! [store js-event clip-uuid]
  (let [dispatch! (:replicant/dispatch! @store)]
    (dispatch! {:replicant/js-event js-event
                :actions [[:editor2/open-sticker-edit-modal clip-uuid]]})))

(defn click-clip!
  "Click on a clip from the design or Moveable. Holds shift/cmd/ctrl to
   toggle membership in the multi-selection; otherwise it replaces the
   selection with just this clip (existing single-select behaviour)."
  [{:keys [store action replicant/js-event]}]
  (let [[_ clip-uuid] action
        modifier? (and js-event
                       (or (.-shiftKey js-event)
                           (.-metaKey js-event)
                           (.-ctrlKey js-event)))
        now (.now js/Date)
        last @last-click-clip
        elapsed-ms (when last (- now (:t last)))
        same-clip? (and last
                        (= (:uuid last) clip-uuid)
                        (= (:modifier? last) modifier?))
        native-double? (native-double-click? js-event)
        duplicate? (and same-clip?
                        (not native-double?)
                        (< elapsed-ms click-dedupe-ms))
        ;; If the first click selected an unselected clip through design.cljs's
        ;; mousedown handler, Moveable did not see that first click and cannot
        ;; set `isDouble` on the second click. Detect that sequence here.
        double-click? (and same-clip?
                           (not modifier?)
                           (or native-double?
                               (and (<= click-dedupe-ms elapsed-ms)
                                    (< elapsed-ms double-click-ms))))]
    (when-not duplicate?
      (reset! last-click-clip {:uuid clip-uuid :modifier? modifier? :t now})
      (cond
        double-click?
        (open-sticker-edit-modal-action! store js-event clip-uuid)

        modifier?
        (do (swap! store toggle-clip-selection clip-uuid)
            (sync-moveable-to-selection! @store)
            (focus-editor-container!))

        :else
        (do (swap! store select-clip clip-uuid)
            (select-movable-layer! clip-uuid))))))

(defn select-multiple-clips!
  "Action handler for the marquee finish. Action shape:
   `[:editor2/select-multiple-clips uuids extend?]` where `extend?` adds to
   the existing selection (shift+marquee)."
  [{:keys [store action]}]
  (let [[_ clip-uuids extend?] action
        f (if extend? extend-selection-with-clips select-multiple-clips)]
    (swap! store f clip-uuids)
    (sync-moveable-to-selection! @store)
    (focus-editor-container!)))

(def sticker-type->modal-id
  {:poll-sticker :modals/editor2-poll-modal
   :question-sticker :modals/editor2-question-modal
   :countdown-sticker :modals/editor2-countdown-modal
   :quiz-sticker :modals/editor2-quiz-modal
   :emoji-slider-sticker :modals/editor2-emoji-slider-modal
   :location-sticker :modals/editor2-location-modal
   :link-sticker :modals/editor2-link-modal
   :music-sticker :modals/editor2-music-modal})

(defn open-sticker-edit-modal!
  "Opens the edit modal for a sticker clip on double-click.
   Looks up the clip's asset type and dispatches the appropriate modal."
  [{:keys [store action]}]
  (let [[_ clip-uuid] action
        state @store
        db (get-in state [:editor2 :db])
        clip (ds/entity db [:clip/uuid clip-uuid])
        asset (:clip/asset clip)
        asset-type (:asset/type asset)
        modal-id (get sticker-type->modal-id asset-type)]
    (when modal-id
      (let [dispatch! (:replicant/dispatch! state)]
        (dispatch! {:actions [[:ui/modal-open
                               modal-id
                               (select-keys asset [:asset/data
                                                   :asset/uuid
                                                   :db/id])]]})))))

(defn inside-slide?
  "Returns true when `position` is inside `slide`. Slide ends are exclusive
   because consecutive slides share a boundary and the boundary belongs to the
   next slide."
  [position slide]
  (and position
       (:slide/start slide)
       (:slide/end slide)
       (<= (:slide/start slide)
           position)
       (< position
          (:slide/end slide))))

(defn get-current-slide
  [state]
  (let [{:keys [playhead-position db]} (:editor2 state)
        slides (common/slide-clips db)]
    (some
      (fn [slide]
        (when (inside-slide? playhead-position
                             slide)
          slide))
      slides)))

(defn get-nearest-slide
  [state]
  (let [{:keys [playhead-position db]} (:editor2 state)
        slides (common/slide-clips db)]
    (first (sort-by (fn [slide]
                      (Math/abs (- (:slide/end slide)
                                   playhead-position)))
                    slides))))

(defn fit-clip
  "Fits the clip vertically and horizontally into the design"
  [state clip-uuid]
  (let [db (get-in state [:editor2
                          :db])
        clip (ds/entity db
                        [:clip/uuid clip-uuid])
        asset (:clip/asset clip)
        transform (:clip/transform clip)
        ;; A zero-sized asset (e.g. metadata not yet loaded) would make the
        ;; division produce `##Inf` and corrupt the transform. Fall back to 1
        ;; so the clip stays at its current scale.
        asset-width (:asset/width asset)
        asset-height (:asset/height asset)
        x-scale (if (and asset-width (pos? asset-width))
                  (/ (:design/width defaults/defaults) asset-width)
                  1)
        y-scale (if (and asset-height (pos? asset-height))
                  (/ (:design/height defaults/defaults) asset-height)
                  1)
        scale (min x-scale y-scale)]
    (-> state
        (transact [{:clip/uuid clip-uuid
                    :clip/transform (merge transform
                                           {:scale [scale scale]})}]))))

(defn center-clip
  "Centers the clip in the design composition."
  [state clip-uuid]
  (let [db (get-in state [:editor2
                          :db])
        clip (ds/entity db
                        [:clip/uuid clip-uuid])
        asset (:clip/asset clip)
        transform (:clip/transform clip)
        [x-scale y-scale] (:scale transform)]
    (-> state
        (transact [{:clip/uuid clip-uuid
                    :clip/transform (merge transform
                                           {:translate [(/ (- (:design/width defaults/defaults)
                                                              (* (:asset/width asset)
                                                                 (or x-scale 1.0))) 2.0)
                                                        (/ (- (:design/height defaults/defaults)
                                                              (* (:asset/height asset)
                                                                 (or y-scale 1.0))) 2.0)]})}]))))

(defn durationless-visual-asset?
  [asset]
  (and (contains? #{:gif
                    :image
                    :text
                    :video}
                  (:asset/type asset))
       (nil? (:asset/duration asset))))

(defn slide-end-default-clip-range
  [{:keys [default-duration min-duration slide start]}]
  (if (inside-slide? start
                     slide)
    (let [slide-end (:slide/end slide)
          end (if (< (- slide-end start)
                     min-duration)
                (+ start min-duration)
                slide-end)]
      {:start start
       :end end})
    {:start start
     :end (+ start default-duration)}))

(defn editable-end
  [{:keys [duration min-duration start end]}]
  (if (and (< (- end start)
              min-duration)
           (or (nil? duration)
               (<= min-duration
                   duration)))
    (+ start min-duration)
    end))

(defn default-asset-clip-range
  [asset slide start]
  (let [default-duration (:clip/default-duration defaults/defaults)
        min-duration (:slide/min-duration defaults/defaults)]
    (if (durationless-visual-asset? asset)
      (slide-end-default-clip-range {:default-duration default-duration
                                     :min-duration min-duration
                                     :slide slide
                                     :start start})
      (let [asset-duration (:asset/duration asset)
            duration (or asset-duration
                         default-duration)
            clip-end (+ start
                        duration)
            end (if (inside-slide? start
                                   slide)
                  (min (:slide/end slide)
                       clip-end)
                  clip-end)]
        {:start start
         :end (if (inside-slide? start
                                 slide)
                (editable-end {:duration duration
                               :min-duration min-duration
                               :start start
                               :end end})
                end)}))))

(defn add-asset-as-clip
  "Adds an asset (image, video, gif) as a new clip on a new track.
  The clip is placed at the playhead position within the current or nearest
  slide. Images, text, GIFs, and GIF-like videos without an intrinsic duration
  end at the slide boundary. Near the end of a slide, clips keep the start
  position and end one second later to avoid hard-to-edit sub-second clips.
  Outside a slide, durationless visual assets use the default duration. Other
  assets use their intrinsic duration (or the default duration) and are clamped
  to the slide boundary only when the clip starts inside a slide. The clip is
  then fitted, centered, and its track is inserted as the topmost track."
  [state {:keys [asset-uuid clip-uuid]}]
  (let [db (get-in state
                   [:editor2
                    :db])
        asset (ds/entity db
                         [:asset/uuid asset-uuid])
        new-track-uuid (random-uuid)
        slide (or (get-current-slide state)
                  (get-nearest-slide state))]
    (if-not slide
      state
      (let [playhead-position (get-in state [:editor2 :playhead-position])
            start (or playhead-position
                      (:slide/start slide)
                      0)
            clip-range (default-asset-clip-range asset
                                                 slide
                                                 start)]
        (-> state
            (transact [(merge {:clip/uuid clip-uuid
                               :clip/asset [:asset/uuid asset-uuid]
                               :clip/name (or (:asset/name asset)
                                              (str "clip "
                                                   clip-uuid))
                               :clip/offset 0
                               :clip/start (:start clip-range)
                               :clip/end (:end clip-range)}
                              (when (= :gif (:asset/type asset))
                                {:clip/loop true}))])
            (fit-clip clip-uuid)
            (center-clip clip-uuid)
            (add-track [nil
                        {:after-track :first
                         :clip/uuid clip-uuid}]
                       new-track-uuid)
            (sort-tracks)
            (select-clip clip-uuid))))))

(defn asset-from-image-url
  [image-url]
  (go
    (let [image (<! (image/load-image image-url))
          asset-uuid (random-uuid)]
      {:asset/width (.-naturalWidth image)
       :asset/height (.-naturalHeight image)
       :asset/uuid asset-uuid
       :asset/type :image
       :asset/uri image-url
       :asset/content-type "image/jpeg"
       :asset/name (str "image-" asset-uuid)})))

(defn asset-from-video-url
  [video-url]
  (go
    (let [video-info (<p! (common/get-video-length {:url video-url}))
          asset-uuid (random-uuid)
          ;; Create a video element to get dimensions
          video (.createElement js/document "video")]
      ;; Only metadata is read here. Avoid `crossOrigin` so redirected blob
      ;; URLs do not require CORS headers on the final GCS object.
      (set! (.-src video) video-url)
      ;; Wait for video to load metadata
      (let [metadata (<p! (js/Promise. (fn [resolve _reject]
                                         (set! (.-onloadedmetadata video)
                                               (fn []
                                                 (resolve {:width (.-videoWidth video)
                                                          :height (.-videoHeight video)}))))))]
        {:asset/width (:width metadata)
         :asset/height (:height metadata)
         :asset/uuid asset-uuid
         :asset/type :video
         :asset/uri video-url
         :asset/content-type "video/mp4"
         :asset/name (str "video-" asset-uuid)
         :asset/duration-ms (:ms video-info)}))))

(defn asset-from-url
  "Creates an asset from a URL, detecting if it's video or image based on extension"
  [url is-video?]
  (if is-video?
    (asset-from-video-url url)
    (asset-from-image-url url)))

(defn fetch-canva-status
  "Fetches whether the user has a connected Canva account using query system"
  [{:keys [store]}]
  (let [dispatch! (:replicant/dispatch! @store)]
    (dispatch! {:actions [[:data/query {:query/kind :query/canva-status}]]})))

(defn canva-connect-success
  "Redirect to Canva OAuth URL after successful command"
  [{:keys [store]}]
  (let [state @store
        result (command/get-latest-result state {:command/kind :command/canva-connect
                                                 :command/data {}})
        auth-url (:authorization_url result)
        code-verifier (:code_verifier result)]
    (when auth-url
      (set! (.-cookie js/document)
            (str "canva-code-verifier=" code-verifier
                 "; path=/; secure; samesite=lax; max-age=600"))
      (js/window.open auth-url "_blank"))))

(defn canva-connect-click
  "Initiates Canva OAuth flow using command system"
  [{:keys [store]}]
  (let [dispatch! (:replicant/dispatch! @store)]
    (dispatch! {:actions [[:data/command
                           {:command/kind :command/canva-connect
                            :command/data {}}
                           {:on-success [[:editor2/canva-connect-success]]}]]})))

(defn canva-edit-design-success
  "Opens Canva editor after design is created/retrieved"
  [{:keys [store]}]
  (let [state @store
        result (command/get-latest-result state {:command/kind :command/canva-create-design
                                                 :command/data {}})
        edit-url (:edit_url result)
        design-id (:design_id result)]

    (when edit-url
      ;; Store design ID for later export
      (swap! store assoc-in [:editor2 :canva/current-design-id] design-id)
      ;; Open Canva editor in new window
      (.open js/window edit-url "_blank"))))

(defn canva-import-actions
  "The actions that add an exported Canva asset to the design. The
   export command copies the export into the blob storage, so the asset
   already has a `/blob/` URI and only older results, which still carry
   the Canva URL, are uploaded by the browser."
  [asset]
  (concat
    [[:editor2/transact-asset [asset]]]
    (when-not (str/starts-with? (:asset/uri asset) "/blob/")
      [[:upload-manager/add-upload
        {:id (:asset/uuid asset)
         :blob-uri (:asset/uri asset)
         :callback-action [:editor2/add-asset-uploaded-uri
                           (:asset/uuid asset)]}]])
    [[:editor2/add-asset-as-clip (:asset/uuid asset)]]))

(defn canva-import-design-success
  "Imports the exported design from Canva"
  [{:keys [store]}]
  (go
    (let [state @store
          result (command/get-latest-result state {:command/kind :command/canva-export-design
                                                   :command/data {:design_id (get-in @store [:editor2 :canva/current-design-id])}})
          export-url (:export_url result)
          export-urls (:export_urls result)
          is-video? (:is_video result)
          design-id (get-in @store [:editor2 :canva/current-design-id])
          dispatch! (:replicant/dispatch! @store)
          ;; Use export_urls if available (multipage), otherwise fall back to export_url
          ;; Note: For videos, there's typically only one URL
          urls-to-import (if (seq export-urls) export-urls [export-url])]

      (when (seq urls-to-import)
        ;; Load all assets in parallel (images or video)
        (let [asset-channels (mapv #(asset-from-url % is-video?) urls-to-import)
              ;; Wait for all channels sequentially (they're already loading in parallel)
              assets (loop [channels asset-channels
                           result []]
                      (if (empty? channels)
                        result
                        (let [asset (<! (first channels))]
                          (recur (rest channels)
                                (conj result (assoc asset :asset/data {:canva/design-id design-id}))))))
              actions (vec (mapcat canva-import-actions
                                   assets))]
          (dispatch! {:actions actions}))))))

(defn canva-import-click
  "Triggers export and import of the current Canva design"
  [{:keys [store]}]
  (let [design-id (get-in @store [:editor2 :canva/current-design-id])
        dispatch! (:replicant/dispatch! @store)]
    (when design-id
      (dispatch! {:actions [[:data/command
                             {:command/kind :command/canva-export-design
                              :command/data {:design_id design-id}}
                             {:on-success [[:editor2/canva-import-design-success]]}]]}))))

(defn canva-sidebar-import-design-success
  "Imports a design from the Canva sidebar after export completes"
  [{:keys [store action]}]
  (go
    (let [state @store
          design-id (second action)
          command-data (nth action 2)
          result (command/get-latest-result state {:command/kind :command/canva-export-design
                                                   :command/data command-data})
          export-url (:export_url result)
          export-urls (:export_urls result)
          is-video? (:is_video result)
          dispatch! (:replicant/dispatch! @store)
          ;; Use export_urls if available (multipage), otherwise fall back to export_url
          ;; Note: For videos, there's typically only one URL
          urls-to-import (if (seq export-urls) export-urls [export-url])]
      (when (seq urls-to-import)
        ;; Load all assets in parallel (images or video)
        (let [asset-channels (mapv #(asset-from-url % is-video?) urls-to-import)
              ;; Wait for all channels sequentially (they're already loading in parallel)
              assets (loop [channels asset-channels
                           result []]
                      (if (empty? channels)
                        result
                        (let [asset (<! (first channels))]
                          (recur (rest channels)
                                (conj result (assoc asset :asset/data {:canva/design-id design-id}))))))
              actions (vec (mapcat canva-import-actions
                                   assets))]
          (dispatch! {:actions actions})))
      ;; Clear loading state
      (swap! store assoc-in [:editor2 :canva/importing-design-id] nil))))

(defn canva-sidebar-import-design
  "Triggers export and import of a Canva design from the sidebar.
   For multi-page designs (page-count > 1), shows page selection UI instead."
  [{:keys [store action]}]
  (let [{:keys [design-id format page-count thumbnail-url title edit-url]} (second action)
        format-preference (if format (name format) "image")
        page-count (or page-count 1)
        dispatch! (:replicant/dispatch! @store)]
    (when design-id
      (if (> page-count 1)
        ;; Multi-page: show page selection panel
        (swap! store assoc-in [:editor2 :canva/page-selection]
               {:design-id design-id
                :page-count page-count
                :format format-preference
                :selected-pages (set (range 1 (inc page-count)))
                :thumbnail-url thumbnail-url
                :title title
                :edit-url edit-url})
        ;; Single page: import directly
        (let [command-data {:design_id design-id
                            :format_preference format-preference}]
          (swap! store assoc-in [:editor2 :canva/importing-design-id] design-id)
          (dispatch! {:actions [[:data/command
                                 {:command/kind :command/canva-export-design
                                  :command/data command-data}
                                 {:on-success [[:editor2/canva-sidebar-import-design-success design-id command-data]]}]]}))))))

(defn canva-cancel-page-selection
  "Returns from page selection view to design list"
  [{:keys [store]}]
  (swap! store update :editor2 dissoc :canva/page-selection))

(defn canva-toggle-page
  "Toggles a page in the page selection set"
  [{:keys [store action]}]
  (let [page-num (second action)]
    (swap! store update-in [:editor2 :canva/page-selection :selected-pages]
           (fn [pages]
             (if (contains? pages page-num)
               (disj pages page-num)
               (conj pages page-num))))))

(defn canva-select-all-pages
  "Selects or deselects all pages"
  [{:keys [store action]}]
  (let [select? (second action)
        page-count (get-in @store [:editor2 :canva/page-selection :page-count])]
    (swap! store assoc-in [:editor2 :canva/page-selection :selected-pages]
           (if select?
             (set (range 1 (inc page-count)))
             #{}))))

(defn canva-confirm-page-selection
  "Confirms page selection and triggers export with selected pages"
  [{:keys [store]}]
  (let [{:keys [design-id format selected-pages]} (get-in @store [:editor2 :canva/page-selection])
        dispatch! (:replicant/dispatch! @store)
        pages (vec (sort selected-pages))
        command-data {:design_id design-id
                      :format_preference format
                      :pages pages}]
    (when (seq pages)
      (swap! store (fn [s]
                     (-> s
                         (assoc-in [:editor2 :canva/importing-design-id] design-id)
                         (update :editor2 dissoc :canva/page-selection))))
      (dispatch! {:actions [[:data/command
                             {:command/kind :command/canva-export-design
                              :command/data command-data}
                             {:on-success [[:editor2/canva-sidebar-import-design-success design-id command-data]]}]]}))))

(defn canva-button-click
  "Opens Canva editor using OAuth/Connect API (replaces deprecated Button API)"
  [{:keys [store]}]
  (let [dispatch! (:replicant/dispatch! @store)]
    ;; Create a new design and open editor
    (dispatch! {:actions [[:data/command
                           {:command/kind :command/canva-create-design
                            :command/data {}}
                           {:on-success [[:editor2/canva-edit-design-success]]}]]})))

(defn add-sticker-asset-as-clip
  [state {:keys [asset-uuid clip-uuid]}]
  (let [db (get-in state
                   [:editor2
                    :db])
        asset (ds/entity db
                         [:asset/uuid asset-uuid])
        new-track-uuid (random-uuid)
        slide (get-nearest-slide state)]
    (-> state
        (transact [{:clip/uuid clip-uuid
                    :clip/asset [:asset/uuid asset-uuid]
                    :clip/name (or (:asset/name asset)
                                   (str "clip "
                                        clip-uuid))
                    :clip/offset 0
                    :clip/start (:slide/start slide)
                    :clip/end (:slide/end slide)}])
        (fit-clip clip-uuid)
        (center-clip clip-uuid)
        (add-track [nil
                    {:after-track :first
                     :clip/uuid clip-uuid}]
                   new-track-uuid)
        (sort-tracks)
        (select-clip clip-uuid))))

(defn add-asset-as-clip!
  [{:keys [store action]}]
  (let [asset-uuid (second action)
        clip-uuid (random-uuid)]
    (swap! store
           add-asset-as-clip
           {:asset-uuid asset-uuid
            :clip-uuid clip-uuid})
    ;; It is important to select the movable layer after the
    ;; state has changed via swap!. Otherwise, there will be no
    ;; movable object that can be selected.
    (select-movable-layer! clip-uuid)
    ))

(defn add-sticker-asset-as-clip!
  [{:keys [store action]}]
  (let [asset-uuid (second action)
        clip-uuid (random-uuid)]
    (swap! store
           add-sticker-asset-as-clip
           {:asset-uuid asset-uuid
            :clip-uuid clip-uuid})
    ;; It is important to select the movable layer after the
    ;; state has changed via swap!. Otherwise, there will be no
    ;; movable object that can be selected.
    (select-movable-layer! clip-uuid)
    ))

(defn q-clip-uuid-of-asset
  [db asset-uuid]
  (ds/q '[:find ?clip-uuid .
          :in $ ?asset-uuid
          :where
          [?asset :asset/uuid ?asset-uuid]
          [?clip :clip/asset ?asset]
          [?clip :clip/uuid ?clip-uuid]]
        db
        asset-uuid))

(defn add-music-audio-clip
  "Adds the companion :audio clip of a music sticker on its own track,
   starting at the slide start. The :clip/offset skips to the selected
   part of the song and `duration` is the selected section length."
  [state {:keys [asset-uuid clip-uuid offset duration]}]
  (let [db (get-in state
                   [:editor2
                    :db])
        asset (ds/entity db
                         [:asset/uuid asset-uuid])
        slide (get-nearest-slide state)]
    (-> state
        (transact [{:clip/uuid clip-uuid
                    :clip/asset [:asset/uuid asset-uuid]
                    :clip/name (or (:asset/name asset)
                                   (str "clip "
                                        clip-uuid))
                    :clip/offset (or offset
                                     0)
                    :clip/start (:slide/start slide)
                    :clip/end (if duration
                                (min (:slide/end slide)
                                     (+ (:slide/start slide)
                                        duration))
                                (:slide/end slide))}])
        (add-track [nil
                    {:after-track :first
                     :clip/uuid clip-uuid}]
                   (random-uuid))
        (sort-tracks))))

(defn add-music-audio-clip!
  [{:keys [store action]}]
  (let [[_ asset-uuid offset duration] action]
    (swap! store
           add-music-audio-clip
           {:asset-uuid asset-uuid
            :clip-uuid (random-uuid)
            :offset offset
            :duration duration})))

(defn ensure-audio-players!
  "Creates the Howl of every audio clip that does not have one yet, so
   that its audio starts downloading and decoding in the background. It
   is dispatched right after a music sticker is added, so the first
   play does not wait several seconds for the freshly created song blob
   to fetch and decode (Howler decodes the whole file before it plays)."
  [{:keys [store]}]
  (audio-player/ensure-audio-players! store))

(defn music-sticker-assets
  "Builds the transaction maps for the music sticker asset and its
   companion :audio asset."
  [{:keys [track browse-session-id alacorn-session-id
           existing-data sticker-asset-uuid audio-asset-uuid
           audio-start-ms audio-duration-ms audio-uri cover-blob-uri]}]
  (let [track* (-> (select-keys track
                                [:id
                                 :audio_asset_id
                                 :title
                                 :display_artist
                                 :cover_artwork_uri
                                 :cover_artwork_thumbnail_uri
                                 :progressive_download_url
                                 :dash_manifest
                                 :duration_in_ms
                                 :highlight_start_times_in_ms
                                 :is_explicit
                                 :has_lyrics])
                   (update :audio_asset_id
                           (fn [audio-asset-id]
                             (or audio-asset-id
                                 (:id track)))))]
    [{:asset/uuid sticker-asset-uuid
      :asset/type :music-sticker
      :asset/name "Music Sticker"
      :asset/data (merge
                    {:track track*
                     :browse-session-id browse-session-id
                     :alacorn-session-id alacorn-session-id
                     :audio-start-ms audio-start-ms
                     :audio-duration-ms audio-duration-ms
                     :audio-asset-uuid audio-asset-uuid
                     :cover-blob-uri cover-blob-uri
                     ;; The design is only rendered for invisible music
                     ;; stickers; all visible ones use a native
                     ;; Instagram visual (display type):
                     :design (or (:design existing-data)
                                 sticker-designs/invisible-music-design)}
                    (when (:invisible existing-data)
                      {:invisible true})
                    (if-let [display-type (:display-type existing-data)]
                      {:display-type display-type}
                      (when-not (:invisible existing-data)
                        {:display-type "MUSIC_OVERLAY_SIMPLE"})))}
     ;; No :asset/uploaded-uri here: the audio already resides at a permanent
     ;; /blob/ URI. Assets with an :asset/uploaded-uri are copied on save by
     ;; fetching them via localhost HTTP (app.editor2.save-command), which
     ;; cannot follow the /blob/ endpoint's redirect to https and would
     ;; silently store a 0-byte copy.
     {:asset/uuid audio-asset-uuid
      :asset/type :audio
      :asset/uri audio-uri
      :asset/content-type "audio/mp4"
      :asset/duration (:duration_in_ms track)
      :asset/name (str (:title track)
                       " – "
                       (:display_artist track))}]))

(defn add-music-sticker!
  "Adds or updates the music sticker asset together with its companion
   :audio asset/clip. The song audio is stored server-side as a blob
   (POST /api/blob/create-from-url), since the Instagram CDN URL of the
   song expires, while the renderer needs the audio to mix it into the
   story video."
  [{:keys [store action]}]
  (let [[_ params] action
        {:keys [track browse-session-id alacorn-session-id existing]} params
        state @store
        db (get-in state [:editor2 :db])
        existing-asset (when-let [uuid (:asset/uuid existing)]
                         (ds/entity db
                                    [:asset/uuid uuid]))
        existing-data (:asset/data existing-asset)
        sticker-asset-uuid (or (:asset/uuid existing-asset)
                               (random-uuid))
        audio-asset-uuid (or (:audio-asset-uuid existing-data)
                             (random-uuid))
        audio-start-ms (js/Math.round (or (:audio-start-ms params)
                                          (first (:highlight_start_times_in_ms track))
                                          0))
        audio-duration-ms (some-> (:audio-duration-ms params)
                                  (js/Math.round))
        dispatch! (:replicant/dispatch! state)]
    (go
      ;; Both blob copies are independent; start them in parallel
      ;; before taking from either channel:
      (let [audio-channel (api/request {:request-method :post
                                        :url "/api/blob/create-from-url"
                                        :transit-params {:blob/url (:progressive_download_url track)
                                                         :blob/content-type "audio/mp4"}})
            ;; The cover artwork is stored as a blob too, so that the
            ;; FabricJS previews of the native Instagram visuals can load
            ;; it same-origin without tainting the canvas:
            cover-url (or (:cover_artwork_uri track)
                          (:cover_artwork_thumbnail_uri track))
            cover-channel (when (seq cover-url)
                            (api/request {:request-method :post
                                          :url "/api/blob/create-from-url"
                                          :transit-params {:blob/url cover-url
                                                           :blob/content-type "image/jpeg"}}))
            blob-uuid (:blob/uuid (:body (<! audio-channel)))
            cover-blob-uuid (when cover-channel
                              (:blob/uuid (:body (<! cover-channel))))]
        (if-not blob-uuid
          (dispatch! {:actions [[:js/alert "Sorry, we could not load the audio of the selected song. Please try again or contact the support."]]})
          (let [db* (get-in @store [:editor2 :db])
                audio-clip-uuid (q-clip-uuid-of-asset db*
                                                      audio-asset-uuid)
                audio-clip (when audio-clip-uuid
                             (ds/entity db*
                                        [:clip/uuid audio-clip-uuid]))
                assets (music-sticker-assets {:track track
                                              :browse-session-id browse-session-id
                                              :alacorn-session-id alacorn-session-id
                                              :existing-data existing-data
                                              :sticker-asset-uuid sticker-asset-uuid
                                              :audio-asset-uuid audio-asset-uuid
                                              :audio-start-ms audio-start-ms
                                              :audio-duration-ms audio-duration-ms
                                              :audio-uri (str "/blob/" blob-uuid)
                                              :cover-blob-uri (when cover-blob-uuid
                                                                (str "/blob/" cover-blob-uuid))})
                tx (cond-> assets
                           audio-clip-uuid
                           (conj (merge
                                   {:clip/uuid audio-clip-uuid
                                    :clip/offset audio-start-ms}
                                   ;; The audio clip keeps its position on the
                                   ;; timeline, only its length follows the
                                   ;; selected section:
                                   (when audio-duration-ms
                                     {:clip/end (+ (:clip/start audio-clip)
                                                   audio-duration-ms)}))))]
            ;; The asset gets a new audio URI, so the cached Howl of the
            ;; clip must be recreated:
            (when audio-clip-uuid
              (audio-player/remove-audio-player! store
                                                 audio-clip-uuid))
            (dispatch! {:actions (concat
                                   [[:editor2/transact-asset tx]
                                    [:editor2/prepare-assets!]]
                                   (when-not existing-asset
                                     [[:editor2/add-sticker-asset-as-clip sticker-asset-uuid]])
                                   (when-not audio-clip-uuid
                                     [[:editor2/add-music-audio-clip audio-asset-uuid audio-start-ms audio-duration-ms]])
                                   ;; Preload the song right after adding, so
                                   ;; the first play is not delayed by the
                                   ;; fetch and decode (see ensure-audio-players!):
                                   [[:editor2/ensure-audio-players]])})))))))

(defn giphy-sidebar-target-clip
  [state]
  (when-let [clip-uuid (get-in state [:editor2 :giphy-sidebar :clip-uuid])]
    (when-let [db (get-in state [:editor2 :db])]
      (ds/entity db
                 [:clip/uuid clip-uuid]))))

(defn open-giphy-sidebar-for-clip
  [state clip-uuid]
  (let [db (get-in state [:editor2 :db])
        asset (:clip/asset (ds/entity db
                                      [:clip/uuid clip-uuid]))]
    (-> state
        (assoc-in [:editor2 :selected-clip] clip-uuid)
        (assoc-in [:editor2 :selected-clips] #{clip-uuid})
        (assoc-in [:editor2 :sidebar-panel] :gif-sidebar)
        (assoc-in [:editor2 :giphy-sidebar]
                  {:clip-uuid clip-uuid
                   :panel-key (or (get-in state [:editor2 :giphy-sidebar :panel-key])
                                  "new")
                   :initial-state (when asset
                                    (giphy-sidebar-state asset))}))))

(defn replace-giphy-sidebar-clip
  [state clip asset]
  (let [clip-uuid (:clip/uuid clip)
        current-asset (:clip/asset clip)]
    (-> state
        (transact [(assoc (dissoc asset :db/id :asset/uuid)
                     :db/id (:db/id current-asset)
                     :asset/uuid (:asset/uuid current-asset))
                   {:clip/uuid clip-uuid
                    :clip/name (:asset/name asset)
                    :clip/loop true}])
        (update-in [:editor2 :video-thumbnails]
                   dissoc
                   (:asset/uuid current-asset))
        (open-giphy-sidebar-for-clip clip-uuid))))

(defn add-giphy-sidebar-clip
  [state asset clip-uuid]
  (-> state
      (transact [asset])
      (add-asset-as-clip {:asset-uuid (:asset/uuid asset)
                          :clip-uuid clip-uuid})
      (transact [{:clip/uuid clip-uuid
                  :clip/loop true}])
      (open-giphy-sidebar-for-clip clip-uuid)))

(defn add-or-replace-giphy-asset!
  [{:keys [store action]}]
  (let [asset (second action)
        giphy-state (nth action 2)
        new-clip-uuid (random-uuid)]
    (let [state (swap! store
                        (fn [state]
                          (if-let [target-clip (giphy-sidebar-target-clip state)]
                            (replace-giphy-sidebar-clip state
                                                        target-clip
                                                        asset)
                            (add-giphy-sidebar-clip state
                                                    asset
                                                    new-clip-uuid))))
          db (get-in state [:editor2 :db])
          clip-uuid (get-in state [:editor2 :giphy-sidebar :clip-uuid])
          asset-uuid (get-in (ds/entity db [:clip/uuid clip-uuid])
                             [:clip/asset :asset/uuid])]
      (write-giphy-sidebar-state! asset-uuid
                                  giphy-state)
      (select-movable-layer! clip-uuid))))

(defn extract-font-families
  "Extracts unique font-family names from a rendered DOM container."
  [container]
  (let [elements (.querySelectorAll container "*")
        fonts (volatile! #{})]
    (.forEach elements
              (fn [el]
                (let [computed (.getComputedStyle js/window el)
                      font-family (.getPropertyValue computed "font-family")]
                  (when (seq font-family)
                    (doseq [family (.split font-family ",")]
                      (let [trimmed (-> family
                                        (.trim)
                                        (.replace (js/RegExp. "^['\"]|['\"]$" "g") ""))]
                        (when (seq trimmed)
                          (vswap! fonts conj trimmed))))))))
    @fonts))

(defn load-fonts
  "Loads the given font families via document.fonts.load. Returns a promise
   that resolves when all fonts have been loaded (or failed)."
  [font-families]
  (js/Promise.allSettled
    (into-array
      (map (fn [family]
             (.load js/document.fonts (str "16px \"" family "\"")))
           font-families))))

(defn get-dimensions
  "Renders hiccup-code into an offscreen container, waits for its fonts to load,
   then measures and returns a promise of {:width :height}."
  [hiccup-code]
  (let [container (.createElement js/document "div")]
    ;; Position container offscreen to avoid affecting layout
    (set! (.-position (.-style container)) "absolute")
    (set! (.-left (.-style container)) "-9999px")
    (set! (.-top (.-style container)) "0")
    (.appendChild js/document.body container)
    ;; Render the Hiccup into the container using rep/render
    (rep/render container hiccup-code)
    ;; Explicitly load the fonts used in the rendered DOM, then measure
    (-> (load-fonts (extract-font-families container))
        (.then (fn [_]
                 ;; Measure before removing from DOM (detached nodes return zero)
                 (let [node (.-firstChild container)]
                   (if node
                     (let [rect (.getBoundingClientRect node)]
                       (.removeChild js/document.body container)
                       {:width (.-width rect)
                        :height (.-height rect)})
                     (do (.removeChild js/document.body container)
                         (throw (js/Error. "get-dimensions: container rendered no child node")))))))
        (.catch (fn [e]
                  ;; Clean up container if .then didn't run (e.g. load-fonts rejected)
                  (when (.-parentNode container)
                    (.removeChild js/document.body container))
                  (throw e))))))

(defn add-text-preset-as-clip
  [state {:keys [text-preset
                 clip-uuid
                 dimensions]}]
  (let [new-asset-uuid (random-uuid)
        new-track-uuid (random-uuid)
        start (or (get-in state
                          [:editor2
                           :playhead-position])
                  0)
        slide (or (get-current-slide state)
                  (get-nearest-slide state))
        clip-range (default-asset-clip-range {:asset/type :text}
                                             slide
                                             start)
        scale text/text-clip-scale]
    (-> state
        (transact [{:db/id "new-text-asset"
                    :asset/uuid new-asset-uuid
                    :asset/type :text
                    :asset/name (:name text-preset)
                    :asset/width (:width dimensions)
                    :asset/height (:height dimensions)
                    :asset/data text-preset}
                   {:clip/uuid clip-uuid
                    :clip/asset [:asset/uuid new-asset-uuid]
                    :clip/name (:text text-preset)
                    :clip/offset 0
                    :clip/start (:start clip-range)
                    :clip/end (:end clip-range)
                    ;; We scale up the element by 4x
                    :clip/transform {:scale [scale scale scale]
                                     :rotate 0}}])
        (center-clip clip-uuid)
        (add-track [nil
                    {:after-track :first
                     :clip/uuid clip-uuid}]
                   new-track-uuid)
        (sort-tracks)
        (select-clip clip-uuid))))

(defn add-text-preset-as-clip!
  [{:keys [store action]}]
  (let [text-preset (second action)
        clip-uuid (random-uuid)]
    (-> (get-dimensions (:hiccup text-preset))
        (.then (fn [dimensions]
                 (swap! store
                        add-text-preset-as-clip {:text-preset text-preset
                                                 :clip-uuid clip-uuid
                                                 :dimensions dimensions})
                 ;; It is important to select the movable layer after the
                 ;; state has changed via swap!. Otherwise, there will be no
                 ;; movable object that can be selected.
                 (select-movable-layer! clip-uuid)))
        (.catch (fn [e]
                  (js/console.error "Failed to add text preset:" e))))))

(defn resize-text-clip!
  [{:keys [store action]}]
  (let [[_ uuid width] action]
    (when (pos? width)
      (let [db (get-in @store [:editor2 :db])
            hiccup (-> (ds/entity db [:clip/uuid uuid])
                       (get-in [:clip/asset :asset/data :hiccup]))
            constrained-hiccup (common/constrain-hiccup-width hiccup width)]
        (-> (get-dimensions constrained-hiccup)
            (.then (fn [dimensions]
                     (swap! store
                            (fn [state]
                              (let [db (get-in state [:editor2 :db])
                                    clip (ds/entity db [:clip/uuid uuid])
                                    asset (:clip/asset clip)
                                    current-data (:asset/data asset)]
                                (transact state
                                          [{:asset/uuid (:asset/uuid asset)
                                            :asset/width (:width dimensions)
                                            :asset/height (:height dimensions)
                                            :asset/data (assoc current-data
                                                          :text-container-width width)}]))))
                     (select-movable-layer! uuid)))
            (.catch (fn [e]
                      (js/console.error "Failed to resize text clip:" e))))))))

(defn shorten-text
  [text n]
  (str (subs text 0 n)
       (when (> (count text) n)
         "...")))

(defn update-text-asset!
  [{:keys [store action]}]
  (let [selected (get-in @store [:editor2 :selected-clip])]
    (when selected
      (let [params (second action)
            db (get-in @store [:editor2 :db])
            clip (ds/entity db [:clip/uuid selected])
            text-container-width (get-in clip [:clip/asset :asset/data :text-container-width])
            hiccup-for-measuring (cond-> (:hiccup params)
                                   text-container-width
                                   (common/constrain-hiccup-width text-container-width))]
        (-> (get-dimensions hiccup-for-measuring)
            (.then (fn [dimensions]
                     (swap! store
                            (fn [state]
                              (if-let [selected (get-in state
                                                        [:editor2
                                                         :selected-clip])]
                                (let [clip (ds/entity (:db (:editor2 state))
                                                      [:clip/uuid selected])
                                      asset (:clip/asset clip)
                                      tcw (get-in asset [:asset/data :text-container-width])]
                                  (transact state
                                            [(when-let [text (:text params)]
                                               {:clip/uuid selected
                                                :clip/name (shorten-text text
                                                                         50)})
                                             {:asset/width (:width dimensions)
                                              :asset/height (:height dimensions)
                                              :asset/uuid (:asset/uuid asset)
                                              :asset/data (cond-> params
                                                            tcw
                                                            (assoc :text-container-width tcw))}]))
                                state)))))
            (.catch (fn [e]
                      (js/console.error "Failed to update text asset:" e))))))))

(defn toggle-text-mark!
  [{:keys [store action]}]
  (let [[_ mark-name attrs] action
        ^js editor-view (get-in @store
                                [:editor2 :text-editor-view])
        sel (.-selection (.-state editor-view))
        mark (aget (.-state editor-view) "config" "schema" "marks" mark-name)
        cmd (if attrs
              (prosemirror-commands/toggleMark mark (clj->js attrs))
              (prosemirror-commands/toggleMark mark))]
    ;; Select all when the selection is empty and the editor
    ;; is not in focus.
    (when (and (.-empty sel)
               (not (.hasFocus editor-view)))
      (prosemirror-commands/selectAll (.-state editor-view)
                                      (fn [tr]
                                        (.dispatch editor-view tr))))
    (when cmd
      (cmd (.-state editor-view)
           (fn [tr]
             (.dispatch editor-view tr))))))

(defn set-text-mark!
  [{:keys [store action]}]
  (let [[_ mark-name attrs] action
        ^js editor-view (get-in @store [:editor2 :text-editor-view])
        ^js editor-state (get-in @store [:editor2 :text-editor-state])
        mark-type (aget editor-state "config" "schema" "marks" mark-name)
        mark (.create mark-type (clj->js attrs))
        sel (.-selection editor-state)
        from (.-from sel)
        to (.-to sel)
        tr (.-tr editor-state)
        pos (.-pos (.-$anchor sel))]

    (cond (and (.-empty sel) (= pos 1))
          ;; When selection is empty and the anchor position is 0,
          ;; select all text and apply the mark
          (let [doc (.-doc editor-state)
                all-selection (new prosemirror-state/AllSelection doc)]
            (.addMark tr (.-from all-selection) (.-to all-selection) mark))

          ;; when the anchor position is somewhere else, the user
          ;; clicked somewhere in the text -> add a stored mark to continue
          ;, with that mark.
          (.-empty sel)
          (.addStoredMark tr mark)

          :else
          (.addMark tr from to mark))
    (.dispatch editor-view
               tr)))


(defn try-read-edn
  [s]
  (try (edn/read-string s)
       (catch :default _e
         nil)))

(defn set-text-mark-or-click!
  [{:keys [action] :as params}]
  (let [[_ _ attrs] action
        value (first (vals attrs))]
    (if-let [selector (:click (try-read-edn value))]
      (.click (js/document.querySelector selector))
      (set-text-mark! params))))

(defn set-text-align!
  "Returns a command that sets the text alignment (e.g. 'left',
   'center', 'right') on all selected paragraphs."
  [{:keys [store action]}]
  (let [align (second action)
        ^js editor-view (get-in @store
                                [:editor2 :text-editor-view])
        ^js editor-state (get-in @store
                                 [:editor2 :text-editor-state])
        tr (.-tr editor-state)
        sel (.-selection editor-state)
        from (.-from sel)
        to (.-to sel)]
    (.nodesBetween (.-doc editor-state) from to
                   (fn [node pos]
                     (when (and (= (.-name (.-type node)) "paragraph")
                                (not= (aget (.-attrs node) "align") align))
                       (let [mapped-pos (.map (.-mapping tr) pos)
                             new-attrs (js/Object.assign #js {}
                                                         (.-attrs node)
                                                         #js {"align" align})]
                         (.setNodeMarkup tr mapped-pos nil new-attrs)))))
    (when (pos? (count (.-steps tr)))
      (.dispatch editor-view tr))))

(defn set-text-line-height!
  "Sets the line-height on all selected paragraphs."
  [{:keys [store action]}]
  (let [line-height (second action)
        ^js editor-view (get-in @store
                                [:editor2 :text-editor-view])
        ^js editor-state (get-in @store
                                 [:editor2 :text-editor-state])
        tr (.-tr editor-state)
        sel (.-selection editor-state)
        doc (.-doc editor-state)
        empty-sel? (.-empty sel)
        from (if empty-sel? 0 (.-from sel))
        to (if empty-sel? (.-size (.-content doc)) (.-to sel))]
    (.nodesBetween doc from to
                   (fn [node pos]
                     (when (and (= (.-name (.-type node)) "paragraph")
                                (not= (aget (.-attrs node) "lineHeight") line-height))
                       ;; Map pos through prior steps — each setNodeMarkup shifts
                       ;; subsequent positions in the transaction.
                       (let [mapped-pos (.map (.-mapping tr) pos)
                             new-attrs (js/Object.assign #js {}
                                                         (.-attrs node)
                                                         #js {"lineHeight" line-height})]
                         (.setNodeMarkup tr mapped-pos nil new-attrs)))))
    (when (pos? (count (.-steps tr)))
      (.dispatch editor-view tr))))

(defn file-url
  "Create an object URL for the file."
  [file]
  (js/URL.createObjectURL file))

(defn asset-type-from-content-type
  [content-type]
  (keyword
    (if (= content-type
           "image/gif")
      "gif"
      (second
        (re-find #"(.*?)/"
                 content-type)))))

(comment
  (map
    asset-type-from-content-type
    ["audio/mpeg"
     "image/jpeg"
     "video/mp4"
     "image/gif"
     ])
  )

(defn get-video-duration [video-file]
  (let [url (js/URL.createObjectURL video-file)
        video (.createElement js/document "video")]
    (set! (.-src video) url)
    (js/Promise.
      (fn [resolve reject]
        (set! (.-onloadedmetadata video)
              (fn []
                (let [duration-ms (* 1000 (.-duration video))]
                  ;; Clean up the temporary URL once done
                  (js/URL.revokeObjectURL url)
                  (resolve duration-ms))))
        (set! (.-onerror video)
              (fn [_e]
                (js/URL.revokeObjectURL url)
                (reject (js/Error. (str "Video \"" (.-name video-file) "\" could not be loaded. The format may not be supported by your browser.")))))))))

(defn get-audio-duration [audio-file]
  (let [url (js/URL.createObjectURL audio-file)
        audio (.createElement js/document "audio")]
    (set! (.-src audio) url)
    (js/Promise.
      (fn [resolve reject]
        (set! (.-onloadedmetadata audio)
              (fn []
                (let [duration-ms (* 1000 (.-duration audio))]
                  (js/URL.revokeObjectURL url)
                  (resolve duration-ms))))
        (set! (.-onerror audio)
              (fn [_e]
                (js/URL.revokeObjectURL url)
                (reject (js/Error. (str "Audio \"" (.-name audio-file) "\" could not be loaded. The format may not be supported by your browser.")))))))))

(defn file-to-asset*
  [file]
  {:asset/uuid (random-uuid)
   :asset/type (asset-type-from-content-type
                 (.-type file))
   :asset/uri (file-url file)
   :asset/content-type (.-type file)
   :asset/name (.-name file)
   })

(defn asset-duration
  [file]
  (case (asset-type-from-content-type
          (.-type file))
    :video
    (.then (get-video-duration file)
           (fn [duration]
             {:asset/duration duration}))
    :audio
    (.then (get-audio-duration file)
           (fn [duration]
             {:asset/duration duration}))
    ;; else:
    (js/Promise.
      (fn [resolve _reject]
        (resolve {})))
    ))

(defn get-video-dimensions [video-file]
  (let [url (js/URL.createObjectURL video-file)
        video (.createElement js/document "video")]
    (set! (.-src video) url)
    (js/Promise.
      (fn [resolve reject]
        (set! (.-onloadedmetadata video)
              (fn []
                (let [width (.-videoWidth video)
                      height (.-videoHeight video)]
                  (js/URL.revokeObjectURL url)
                  (resolve {:width width :height height}))))
        (set! (.-onerror video)
              (fn [_e]
                (js/URL.revokeObjectURL url)
                (reject (js/Error. (str "Video \"" (.-name video-file) "\" could not be loaded. The format may not be supported by your browser.")))))))))

(defn get-image-dimensions [image-file]
  (let [url (js/URL.createObjectURL image-file)
        img (.createElement js/document "img")]
    (set! (.-src img) url)
    (js/Promise.
      (fn [resolve reject]
        (set! (.-onload img)
              (fn []
                (let [width (.-naturalWidth img)
                      height (.-naturalHeight img)]
                  (js/URL.revokeObjectURL url)
                  (resolve {:width width :height height}))))
        (set! (.-onerror img)
              (fn [_e]
                (js/URL.revokeObjectURL url)
                (reject (js/Error. (str "Image \"" (.-name image-file) "\" could not be loaded. The file may be corrupted.")))))))))

(defn validate-image-file
  "Validates an image file for size and dimensions.
   Returns a promise that resolves to {:valid? bool :error string-or-nil}"
  [file]
  (let [file-size (.-size file)
        file-name (.-name file)]
    (if (> file-size media-limits/max-image-file-size-bytes)
      (js/Promise.resolve
        {:valid? false
         :error (str "Image \"" file-name "\" exceeds 20 MB. Please use a smaller image.")})
      (-> (get-image-dimensions file)
          (.then (fn [{:keys [width height]}]
                   (if (or (> width media-limits/max-image-dimension-pixels)
                           (> height media-limits/max-image-dimension-pixels))
                     {:valid? false
                      :error (str "Image \"" file-name "\" exceeds 8000x8000 pixels (" width "x" height "). Please use a smaller image.")}
                     {:valid? true})))
          (.catch (fn [_]
                    {:valid? false
                     :error (str "Image \"" file-name "\" could not be loaded. The file may be corrupted.")}))))))

(defn asset-dimensions
  [file]
  (case (asset-type-from-content-type
          (.-type file))
    :video
    (.then (get-video-dimensions file)
           (fn [{:keys [width height]}]
             (if (> width 1080)
               {:asset/width 1080
                :asset/height (* 1080 (/ height width))}
               {:asset/width width
                :asset/height height})))
    (:image :gif)
    (.then (get-image-dimensions file)
           (fn [{:keys [width height]}]
             (if (> width 1080)
               {:asset/width 1080
                :asset/height (* 1080 (/ height width))}
               {:asset/width width
                :asset/height height})))
    ;; else:
    (js/Promise.
      (fn [resolve _reject]
        (resolve {})))
    ))

(def supported-asset-types
  #{:video :audio :image :gif})

(defn file-to-asset
  [file]
  (let [asset-type (asset-type-from-content-type (.-type file))]
    (if (supported-asset-types asset-type)
      (-> (js/Promise.all
            [(asset-duration file)
             (asset-dimensions file)])
          (.then (fn [maps]
                   (apply merge
                          (file-to-asset* file)
                          maps))))
      (js/Promise.reject
        (js/Error. (str "File \"" (.-name file) "\" is not supported. Supported types: video, audio, image, gif."))))))

(defn- image-file?
  [file]
  (#{:image :gif} (asset-type-from-content-type (.-type file))))

(defn- validate-files
  "Validates files, returning a promise that resolves to {:valid [files] :errors [strings]}"
  [files]
  (let [files-seq (seq files)
        image-files (filter image-file? files-seq)
        other-files (remove image-file? files-seq)]
    (.then (js/Promise.all (map validate-image-file image-files))
           (fn [validation-results]
             (let [file-results (map vector image-files validation-results)
                   valid-images (->> file-results
                                     (filter (fn [[_ result]] (:valid? result)))
                                     (map first))
                   errors (->> file-results
                               (filter (fn [[_ result]] (not (:valid? result))))
                               (map (fn [[_ result]] (:error result))))]
               {:valid (concat other-files valid-images)
                :errors errors})))))

(defn add-upload!
  [{:keys [store action]}]
  (let [files (second action)
        dispatch! (:replicant/dispatch! @store)]
    (.then (validate-files files)
           (fn [{:keys [valid errors]}]
             ;; Show error alerts for invalid files
             (doseq [error errors]
               (dispatch! {:actions [[:js/alert error]]}))
             ;; Process valid files
             (when (seq valid)
               (-> (js/Promise.all (map file-to-asset valid))
                   (.then
                     (fn [assets]
                       (let [db (get-in (swap! store
                                               transact
                                               (vec assets))
                                        [:editor2
                                         :db])]
                         (doseq [asset (map
                                         (fn [{:keys [asset/uuid]}]
                                           (ds/entity db
                                                      [:asset/uuid uuid]))
                                         assets)]
                           ;; Upload assets and update assets with new :asset/uri from upload
                           (dispatch! {:actions [[:upload-manager/add-upload
                                                  {:id (:asset/uuid asset)
                                                   :blob-uri (:asset/uri asset)
                                                   :callback-action [:editor2/add-asset-uploaded-uri
                                                                     (:asset/uuid asset)]}]
                                                 [:editor2/add-asset-as-clip (:asset/uuid asset)]]})
                           (extension-points/prepare-asset!
                             {:asset asset
                              :store store})))))
                   (.catch
                     (fn [e]
                       (dispatch! {:actions [[:js/alert (.-message e)]]})))))))))
(defn manage-play-state!
  [{:keys [store]}]
  (let [state @store
        db (get-in state
                   [:editor2
                    :db])
        playhead-position (get-in state
                                  [:editor2
                                   :playhead-position])]
    (doseq [clip-uuid (common/q-all-clips db)]
      (let [clip (ds/entity db
                            [:clip/uuid clip-uuid])
            playing (<= (:clip/start clip)
                        playhead-position
                        (:clip/end clip))
            asset-type (get-in clip
                               [:clip/asset
                                :asset/type])]
        (case asset-type
          (:video :hyperframes)
          (doseq [element (media-elements clip)]
            (set! (.-muted element) (boolean (:clip/muted clip)))
            (if playing
              (when (.-paused element)
                (seek-video! {:state state
                              :clip clip})
                ;; A <video> returns a promise, the <hyperframes-player>
                ;; of a template clip returns nothing:
                (some-> (.play element)
                        (.catch (fn [e]
                                  (when-not (= "NotAllowedError" (.-name e))
                                    (js/console.warn "Video playback failed:" e))))))
              (when-not (.-paused element)
                (.pause element))))

          :audio
          ;; Howler queues every call on a Howl that is still loading and
          ;; drains the queue recursively once it loaded. Calling it on
          ;; every tick of the play loop overflows the stack
          ;; (STORRITO-V2-KW), so the clip joins in after its audio loaded:
          (when-let [howl (get-in state
                                  [:editor2
                                   :audio-players
                                   (:clip/uuid clip)])]
            (when (= "loaded" (.state howl))
              (.mute howl (boolean (:clip/muted clip)))
              (if playing
                (when-not (.playing howl)
                  (seek-audio! {:state state
                                :clip clip})
                  (.play howl))
                (when (.playing howl)
                  (.pause howl)))))

          ;; else:
          nil)))))

(defn pause-elements!
  [{:keys [store]}]
  (let [state @store
        db (get-in state
                   [:editor2
                    :db])]
    (doseq [clip-uuid (common/q-all-clips db)]
      (let [clip (ds/entity db
                            [:clip/uuid clip-uuid])
            asset-type (get-in clip
                               [:clip/asset
                                :asset/type])]
        (case asset-type
          (:video :hyperframes)
          (doseq [element (media-elements clip)]
            (.pause element))

          :audio
          (when-let [howl (get-in state
                                  [:editor2
                                   :audio-players
                                   (:clip/uuid clip)])]
            (.pause howl))

          ;; else:
          nil)))))

(defn pause!
  [{:keys [store] :as params}]
  (when-let [play-interval-id (get-in @store
                                      [:editor2
                                       :play-interval-id])]
    (js/clearInterval play-interval-id)
    (let [state (swap! store
                       update
                       :editor2
                       (fn [editor2]
                         (-> editor2
                             (dissoc :play-interval-id)
                             (assoc :status :paused))))]
      (pause-elements! params)
      (seek! state))))

(defn stop!
  [{:keys [store] :as params}]
  (when-let [play-interval-id (get-in @store
                                      [:editor2
                                       :play-interval-id])]
    (js/clearInterval play-interval-id))
  (let [state (swap! store
                     (fn [state]
                       (-> state
                           (update :editor2
                                   (fn [editor2]
                                     (-> editor2
                                         (dissoc :play-interval-id)
                                         (assoc :status :paused
                                                :playhead-position 0))))
                           (manage-selected-clip))))]
    (pause-elements! params)
    (seek! state)))

(defn play!
  [{:keys [store] :as params}]
  (audio-player/ensure-audio-players! store)
  (when-not (get-in @store
                    [:editor2
                     :play-interval-id])
    (let [start (js/performance.now)
          {:keys [db playhead-position]} (:editor2 @store)
          start-playhead-position playhead-position
          interval-id (js/setInterval
                        (fn []
                          (let [new-playhead-position (long
                                                        (+ start-playhead-position
                                                           (- (js/performance.now)
                                                              start)))]
                            (if (> new-playhead-position (timeline/q-total-duration db))
                              (stop! params)
                              (do
                                (swap! store
                                       assoc-in
                                       [:editor2
                                        :playhead-position]
                                       new-playhead-position)
                                (manage-play-state! params)))))
                        (/ 1000 60.0))]
      (seek! @store)
      (swap! store
             (fn [state]
               (-> state
                   (update :editor2
                           assoc
                           :play-interval-id interval-id
                           :status :playing)
                   (update :editor2
                           dissoc
                           :selected-clip)))))))

(defn click-time-selector
  [{:keys [state replicant/js-event]}]
  (let [canvas (.-target js-event)
        rect (.getBoundingClientRect canvas)
        ;; Calculate x offset: click position relative to the canvas's left edge
        x (- (.-clientX js-event) (.-left rect))
        pixel-per-second (get-in state
                                 [:editor2
                                  :pixel-per-second])
        milliseconds (pixel-to-ms pixel-per-second
                                  x)]
    (-> state
        (assoc-in [:editor2 :playhead-position]
                  milliseconds)
        (manage-selected-clip))
    ))

(defn q-assets
  [db]
  (ds/q
    '[:find
      [?asset-uuid ...]
      :where
      [_ :asset/uuid ?asset-uuid]]
    db))

(defn get-assets
  [state]
  (let [db (get-in state
                   [:editor2
                    :db])]
    (map (fn [asset-uuid]
           (ds/entity db
                      [:asset/uuid asset-uuid]))
         (q-assets db))))

(defn q-image-clips-with-legacy-filters
  "Returns all image clip entities that have legacy filters."
  [db]
  (map (fn [eid]
         (ds/entity db eid))
       (ds/q '[:find [?clip ...]
               :where
               [?clip :clip/asset ?asset]
               [?asset :asset/type :image]
               [?asset :asset/data ?data]
               [(get ?data :legacy-filters) ?legacy-filters]
               (not [?asset :asset/uploaded-uri])]
             db)))

(defn render-blurred-image-to-canvas
  "Renders an image asset with legacy filters applied to a canvas element.
   Returns a Promise that resolves to a base64 data URL."
  [asset]
  (js/Promise.
    (fn [resolve reject]
      (let [img (js/Image.)
            width (:design/width defaults/defaults)
            height (:design/height defaults/defaults)]

        ;; Handle image load
        (set! (.-onload img)
              (fn []
                (let [canvas (-> (image/make-canvas {:width width
                                                     :height height})
                                 (image/background-color editor.model/default-background-color)
                                 (image/draw-blurry-background img)
                                 )
                      data-url (.toDataURL canvas "image/png")]
                  ;; Convert canvas to base64 data URL
                  (resolve {:asset (assoc (into {} asset)
                                     :asset/width width
                                     :asset/height height)
                            :content-type "image/png"
                            :data-url data-url}))))
        ;; Handle image load errors
        (set! (.-onerror img)
              (fn [error]
                (js/console.warn "Failed to load image:" (:asset/uri asset) error)
                (resolve {:error true
                          :asset (into {} asset)})))

        ;; Start loading the image
        (set! (.-crossOrigin img) "anonymous")
        (set! (.-src img) (:asset/uri asset))))))

(defn screenshot-images-with-legacy-filters!
  [{:keys [store]}]
  (let [db (get-in @store
                   [:editor2
                    :db])
        image-clips (q-image-clips-with-legacy-filters db)
        promises (doall (map
                          (fn [clip]
                            (-> (render-blurred-image-to-canvas (:clip/asset clip))
                                (.then (fn [result]
                                         (assoc result
                                                :clip/uuid (:clip/uuid clip))))))
                          image-clips))
        dispatch! (:replicant/dispatch! @store)]
    (-> (js/Promise.all promises)
        (.then (fn [results]
                 (doseq [result results]
                   (let [asset (into {} (:asset result))]
                     (if (:error result)
                       ;; Remove the legacy assets and clips to start with a clean design
                       (dispatch! {:actions [[:editor2/retract-entity [:clip/uuid (:clip/uuid result)]]
                                             [:editor2/retract-entity [:asset/uuid (:asset/uuid asset)]]]})
                       (dispatch! {:actions [[:upload-manager/add-upload
                                              {:id (:asset/uuid asset)
                                               :blob-uri (:data-url result)
                                               :callback-action [:editor2/handle-legacy-filter-asset
                                                                 asset]
                                               :failure-action [:editor2/drop-legacy-filter-asset
                                                                asset]}]]})))))))))

(defn prepare-assets!
  [{:keys [store]}]
  (js/Promise.all
    (cons
      (js/Promise.resolve
        ;; Check if we need to generate blurred images
        (when (common/needs-blurred-images @store)
          (screenshot-images-with-legacy-filters! {:store store})))
      ;; Continue with existing asset preparation logic
      (map
        (fn [asset]
          (js/Promise.resolve
            (extension-points/prepare-asset!
              {:asset asset
               :store store})))
        (get-assets @store))))
  )

(defmethod extension-points/prepare-asset!
  :default
  [_params]
  nil)

(defmethod extension-points/prepare-asset!
  :text
  [{:keys [asset store]}]
  (when-let [hiccup (get-in asset [:asset/data :hiccup])]
    (let [text-container-width (get-in asset [:asset/data :text-container-width])
          hiccup-for-measuring (cond-> hiccup
                                 text-container-width
                                 (common/constrain-hiccup-width text-container-width))]
      (-> (get-dimensions hiccup-for-measuring)
          (.then (fn [dimensions]
                   (swap! store
                          (fn [state]
                            (transact state
                                      [{:asset/uuid (:asset/uuid asset)
                                        :asset/width (:width dimensions)
                                        :asset/height (:height dimensions)}])))))
          (.catch (fn [e]
                    (js/console.error "Failed to prepare text asset:" e)))))))

(defn wrap-swap
  [f]
  (fn [{:keys [store]}]
    (swap! store
           f)))

(defn wrap-params-swap
  [f]
  (fn [params]
    (swap! (:store params)
           (fn [state]
             (f (assoc params
                  :state
                  state))))))

(defn export-design!
  "Exports the editor2 design as ZIP file. It will include a design.edn with the
   DataScript :db. Optionally, the ZIP file allows to include further assets."
  [{:keys [store]}]
  (let [state @store
        edn-data (pr-str (select-keys (:editor2 state)
                                      [:db]))
        blob (js/Blob. #js [edn-data] #js {:type "application/edn"})]
    (.then (common/create-zip
             {"design.edn" blob})
           (fn [zip-blob]
             (common/download! {:content zip-blob
                                :file-name "storrito-design.zip"})))))

(defn set-asset-design
  [{:keys [action state]}]
  (let [[_ data] action]
    (if-let [selected-clip (common/get-selected-clip state)]
      (let [asset (:clip/asset selected-clip)
            tx [{:db/id (:db/id asset)
                 :asset/data (-> (:asset/data asset)
                                 (dissoc :invisible
                                         :display-type)
                                 (assoc :design data))}]]
        (transact state
                  tx))
      state)))

(defn set-asset-invisible-design
  [{:keys [action state]}]
  (let [[_ data] action]
    (if-let [selected-clip (common/get-selected-clip state)]
      (let [asset (:clip/asset selected-clip)
            tx [{:db/id (:db/id asset)
                 :asset/data (-> (:asset/data asset)
                                 (dissoc :display-type)
                                 (assoc :invisible true
                                        :design data))}]]
        (transact state
                  tx))
      state)))

(defn set-asset-display-type
  "Selects a native Instagram visual (display type) for the selected
   music sticker. The Instagram app renders the visual itself on the
   published story; the renderer excludes the sticker from the baked
   media."
  [{:keys [action state]}]
  (let [[_ display-type] action]
    (if-let [selected-clip (common/get-selected-clip state)]
      (let [asset (:clip/asset selected-clip)
            tx [{:db/id (:db/id asset)
                 :asset/data (-> (:asset/data asset)
                                 (dissoc :invisible)
                                 (assoc :display-type display-type))}]]
        (transact state
                  tx))
      state)))

(defn toggle-mute
  [state clip-uuid]
  (let [db (get-in state [:editor2 :db])
        clip (ds/entity db [:clip/uuid clip-uuid])
        muted (not (:clip/muted clip))]
    (transact state
              [{:clip/uuid clip-uuid
                :clip/muted muted}])))

(defn adapt-clip-data
  [{:keys [action state]}]
  (let [[_ data] action
        clip-uuid (get-in state [:editor2
                                 :selected-clip])]
    (if clip-uuid
      (transact state
                [(merge {:clip/uuid clip-uuid}
                        data)])
      state)))

(defn set-background-color
  [{:keys [state action]}]
  (if-let [slide-uuid (common/current-slide-uuid state)]
    (transact state
              [{:slide/uuid slide-uuid
                :slide/background-color (second action)}])
    state))

(def import-failed-alert-text
  "Loading the media failed, please reload the page. Contact support if this issue persists.")

(def media-link-invalid-alert-text
  "This link does not point to a media. Please open the media from the gallery.")

(defn editor1-import!
  "Imports a gallery media without an editor2 design.
   `ui.editor2.timeline/on-load-actions` keeps the loading panel up
   until the import arrives. A failed import would leave the panel up
   forever, so the user gets an alert. The error is rethrown to stay
   visible as an unhandled rejection.

   A link without a numeric media eid (`?editor1-import=undefined`,
   STORRITO-V2-MB) has nothing to import: reloading would not help, so
   the user gets an alert and is sent to the gallery instead, like
   `load!` does for an invalid design id."
  [{:keys [store action]}]
  (let [params (second action)
        story-media-eid (:story-media/eid params)]
    (if-not (number? story-media-eid)
      (let [dispatch! (:replicant/dispatch! @store)]
        (dispatch! {:actions [[:js/alert media-link-invalid-alert-text]
                              [:js/location "/ui/gallery"]]}))
      (.then (editor1-import/prepare-import {:story-media/eid story-media-eid})
             (fn [w]
               (let [dispatch! (:replicant/dispatch! @store)]
                 (dispatch! {:actions [[:store/assoc :editor2
                                        (merge defaults/defaults
                                               {:db (:db w)
                                                :story-media/eid story-media-eid
                                                :story-post/eid (:story-post/eid params)
                                                :tiktok-post/eid (:tiktok-post/eid params)})]
                                       [:editor2/prepare-assets!]]})))
             (fn [error]
               (let [dispatch! (:replicant/dispatch! @store)]
                 (dispatch! {:actions [[:js/alert import-failed-alert-text]]}))
               (throw error))))))

(defn with-current-schema
  "A saved design carries the schema it was saved with. Attributes that
   became identities or refs since then (e.g. :word/uuid, :asset/words)
   would not upsert or resolve in such a db, so the db is rebuilt from
   its datoms with the current schema."
  [db]
  (if (= (:schema db) schema/schema)
    db
    (ds/init-db (ds/datoms db :eavt)
                schema/schema)))

(def design-link-invalid-alert-text
  "This link does not point to a design. Please open the design from the gallery.")

(defn design-blob-id?
  "Whether the `design-blob-id` query-param is a blob UUID. Links with a
   missing or made-up id (e.g. `design-blob-id=unknown`) reach the
   editor too, and `/blob/<id>` would answer with a 404."
  [design-blob-id]
  (boolean (and (string? design-blob-id)
                (parse-uuid design-blob-id))))

(defn design-db
  "The DataScript db in the text of a design blob, nil when the text is
   not a saved design. `/blob/<id>` redirects to the public storage URL,
   so a filtering network (or anything else that answers the fetch with
   a 200 that is not the blob) hands the editor e.g. an HTML page. Read
   as EDN that is a symbol, nil or throws, and `(:db ...)` of it is nil,
   which `with-current-schema` could not rebuild (STORRITO-V2-M7)."
  [text]
  (let [design (try
                 (edn/read-string text)
                 (catch :default _
                   nil))]
    (when (and (map? design)
               (ds/db? (:db design)))
      (:db design))))

(def design-load-failed-alert-text
  "The design could not be loaded. Please check your internet connection and open the design from the gallery again.")

(defn report-design-load-failure!
  "Reports a `/blob/<id>` response that is no design, with what it was
   instead, so the cause can be seen in Sentry."
  [response text]
  (let [error (js/Error. (str "Design blob is not a design: "
                              (pr-str {:status (.-status response)
                                       :content-type (.get (.-headers response)
                                                           "content-type")
                                       :length (count text)
                                       :head (subs text
                                                   0
                                                   (min 120
                                                        (count text)))})))]
    (js/console.error error)
    (when (exists? js/Sentry)
      (js/Sentry.captureException error))))

(defn load!
  "Loads the design of `:design-blob/id`. An invalid id, or a response
   that is no design, would leave the loading panel up forever, so the
   user gets an alert and is sent to the gallery instead."
  [{:keys [store action]}]
  (let [params (second action)
        dispatch! (:replicant/dispatch! @store)]
    (if-not (design-blob-id? (:design-blob/id params))
      (dispatch! {:actions [[:js/alert design-link-invalid-alert-text]
                            [:js/location "/ui/gallery"]]})
      (-> (js/fetch (str "/blob/" (:design-blob/id params)))
          (.then (fn [response]
                   (if (.-ok response)
                     (.then (.text response)
                            (fn [text]
                              (if-let [db (some-> (design-db text)
                                                  (with-current-schema))]
                                (dispatch! {:actions [[:store/assoc :editor2
                                                       (merge defaults/defaults
                                                              {:db db
                                                               :db-snapshot-hash (hash db)
                                                               :story-media/eid (:story-media/eid params)
                                                               :story-post/eid (:story-post/eid params)
                                                               :tiktok-post/eid (:tiktok-post/eid params)})]
                                                      [:editor2/prepare-assets!]]})
                                (do
                                  (report-design-load-failure! response text)
                                  (dispatch! {:actions [[:js/alert design-load-failed-alert-text]
                                                        [:js/location "/ui/gallery"]]})))))
                     (js/Promise.reject
                       (js/Error. (str "Fetch failed: " (.-status response)))))
                   ))))
    ))

(defn add-asset-uploaded-uri
  [{:keys [state action]}]
  (let [[_ asset-uuid upload-result] action]
    (transact state
              [{:asset/uuid asset-uuid
                :asset/uploaded-uri (:url upload-result)}])))

(defn handle-legacy-filter-asset
  [{:keys [state action]}]
  (let [[_ asset upload-result] action
        db (get-in state [:editor2 :db])
        ;; Joins via the uuid instead of a lookup ref: the asset can be
        ;; gone by the time the upload callback arrives (deleted or the
        ;; design was reloaded), and a lookup ref to a missing entity
        ;; makes the query throw (STORRITO-V2-F1).
        clip-uuid (ds/q '[:find ?clip-uuid .
                          :in $ ?asset-uuid
                          :where
                          [?asset :asset/uuid ?asset-uuid]
                          [?clip :clip/asset ?asset]
                          [?clip :clip/uuid ?clip-uuid]]
                        db
                        (:asset/uuid asset))]
    (if clip-uuid
      (-> state
          (transact [(-> asset
                         (update :asset/data
                                 dissoc
                                 :legacy-filters)
                         (assoc :asset/uri (:url upload-result)
                                :asset/content-type "image/png"
                                :asset/uploaded-uri (:url upload-result)))])
          (fit-clip clip-uuid)
          (center-clip clip-uuid))
      ;; No clip, remove asset since no longer needed.
      ;; The user deleted the clip before legacy filter was uploaded properly.
      (transact state
                [[:db/retractEntity [:asset/uuid (:asset/uuid asset)]]])
      )))

(defn drop-legacy-filter-asset
  "The upload of the blurry background stalled and was given up by
   `ui.upload-manager/fail-upload!`. The background is decoration, so
   its clips and the asset are removed instead of keeping Save and
   Share disabled by `common/needs-blurred-images` forever (brain-90).
   The same clean-up as for a background image that fails to load."
  [{:keys [state action]}]
  (let [[_ asset _failure] action
        db (get-in state [:editor2 :db])
        asset-eid (ds/q '[:find ?asset .
                          :in $ ?asset-uuid
                          :where
                          [?asset :asset/uuid ?asset-uuid]]
                        db
                        (:asset/uuid asset))
        clip-eids (when asset-eid
                    (ds/q '[:find [?clip ...]
                            :in $ ?asset
                            :where
                            [?clip :clip/asset ?asset]]
                          db
                          asset-eid))]
    (if asset-eid
      (transact state
                (mapv (fn [eid]
                        [:db/retractEntity eid])
                      (conj (vec clip-eids)
                            asset-eid)))
      state)))

(defn retract-entity
  "Retracts an entity from the db."
  [{:keys [state action]}]
  (transact state
            [[:db/retractEntity (second action)]]))

(defn set-query-params!
  "Replace the current URL's query-string with map m without reloading
   and without adding a new history entry."
  [m]
  (let [url (js/URL. (.-href js/location))]
    (set! (.-search url) "")
    (let [sp (.-searchParams url)]
      (doseq [[k v] m]
        (.set sp (name k) v)))
    (.replaceState js/history
                   nil
                   ""
                   (str url))))

(defn saved
  [{:keys [state action]}]
  (let [command (second action)
        command-result (command/get-latest-result state
                                                  command)
        editor2-state (:editor2 state)]
    (set-query-params! (cond-> {:story-media-eid (:story-media/eid command-result)
                                :design-blob-id (:design-blob-id command-result)}
                         (:story-post/eid editor2-state)
                         (assoc :story-post-eid (:story-post/eid editor2-state))

                         (:tiktok-post/eid editor2-state)
                         (assoc :tiktok-post-eid (:tiktok-post/eid editor2-state))))
    (assoc-in
      state
      [:editor2 :story-media/eid]
      (:story-media/eid command-result))))

(defn handle-keydown
  [params e]

  #_(prn (.-keyCode e) " - " js/document.activeElement.id)

  (let [store (:ui/store params)
        dispatch! (:replicant/dispatch! @store)]
    (when (= (get-in @store [:location :location/page-id])
             :pages/editor2)
      (cond
        ;; Del Key
        (and (= (.-keyCode e) 46)
             (= js/document.activeElement.id
                composition/editor-container-id))
        (do (.preventDefault e)
            (swap! store
                   (fn [state]
                     (-> state
                         delete-clip
                         remove-empty-tracks
                         sort-tracks
                         (assoc-in [:editor2 :sidebar-panel] :media)))))

        (and (= (.-keyCode e) 32)
             (= js/document.activeElement.id "timeline"))
        (do (.preventDefault e)
            (dispatch! {:actions (if (= (get-in @store [:editor2
                                                        :status])
                                        :paused)
                                   [[:editor2/play]]
                                   [[:editor2/pause]])}))

        (and (.-ctrlKey e)
             (= (.-keyCode e) 75)
             (or (= js/document.activeElement.id "timeline")
                 (= js/document.activeElement.id
                    composition/editor-container-id)))
        (do (.preventDefault e)
            (dispatch! {:actions [[:editor2/cut-clip]]}))

        ))))

(defn register-hotkeys
  [params]
  (let [store (:ui/store params)]
    (when-not (::hotkeys-registered? @store)
      (.addEventListener js/window "keydown" (partial handle-keydown params))
      (swap! store assoc ::hotkeys-registered? true))))

(defn deselect-clips
  [{:keys [store action replicant/js-event]}]
  (let [movable @(second action)
        id (.-id (.-target js-event))]
    (when (some #{id} ["editor-container"
                       "overlay-mask"
                       "composition-design"])
      (composition/clear-movable-selection! movable)
      (swap! store
             update :editor2
             (fn [editor2]
               (-> editor2
                   (dissoc :selected-clip :sidebar-panel)
                   (assoc :selected-clips #{})))))))

(def saving-failed-alert-text
  "Saving failed, please try again. Contact support if this issue persists.")

(defn media-not-uploaded-alert-text
  "Names the media whose upload `save!` started again."
  [assets]
  (str "This media was not uploaded yet: "
       (str/join ", "
                 (map (fn [asset]
                        (str "\""
                             (or (:asset/name asset)
                                 (name (:asset/type asset :media)))
                             "\""))
                      assets))
       ". The upload was started again. Please save once it has finished."))

(defn upload-again-actions
  "The actions that upload the media of the `assets` again. Their
   `blob:` URL can still be read as long as the browser tab that created
   it is open; `ui.upload-manager/add-upload!` tells the user when it
   cannot."
  [assets]
  (mapv (fn [asset]
          [:upload-manager/add-upload
           {:id (:asset/uuid asset)
            :blob-uri (:asset/uri asset)
            :callback-action [:editor2/add-asset-uploaded-uri
                              (:asset/uuid asset)]}])
        assets))

(defn save-design!
  [{:keys [store action]}]
  (let [params (second action)
        dispatch! (:replicant/dispatch! @store)]
    (swap! store assoc-in [:editor2 :saving] {})
    (let [editor2-state (:editor2 @store)
          design-db (:db editor2-state)
          save-command {:command/kind :command/save-editor2
                        :command/data {:story-media/design {:db design-db}
                                       :story-media/eid (:story-media/eid editor2-state)
                                       :story-post/eid (:story-post/eid editor2-state)
                                       :tiktok-post/eid (:tiktok-post/eid editor2-state)}}
          command-action [:data/command save-command
                          {:on-success [[:store/assoc-in [:editor2 :db-snapshot-hash] (hash design-db)]
                                        [:editor2/saved save-command]
                                        [:store/assoc-in [:editor2 :saving] nil]
                                        (when-let [on-success-action (:on-success-action params)]
                                          [on-success-action save-command])]
                           :on-error [[:store/assoc-in [:editor2 :saving] nil]
                                      [:js/alert saving-failed-alert-text]]}]]
      (swap! store assoc-in
             [:editor2 :saving :command]
             save-command)
      (dispatch! {:actions [command-action]}))))

(defn save!
  "Saves the design, unless a clip shows media that never reached the
   server. An upload that was given up (`ui.upload-manager/fail-upload!`)
   leaves its asset with the `blob:` URL of this browser tab, and the
   Save and Share buttons are enabled again once no upload is running.
   Saved like that, the design renders with the 'INVALID' placeholder of
   MLT in place of the media (brain-104). The server refuses such a
   design as well (`app.editor2.save-command/assets-not-uploaded`).

   Instead of saving, the upload of that media is started again, so the
   user neither loses the design nor has to add the media a second
   time. The buttons show 'Uploading media...' until it has finished."
  [{:keys [store]
    :as w}]
  (if-let [assets (seq (local-assets/q-local-only-assets
                         (get-in @store [:editor2 :db])))]
    ((:replicant/dispatch! @store)
     {:actions (conj (upload-again-actions assets)
                     [:js/alert (media-not-uploaded-alert-text assets)])})
    (save-design! w)))

(defn schedule-callback!
  [{:keys [store action]
    :as w}]
  (let [save-command (second action)
        command-result (command/get-latest-result @store
                                                  save-command)
        story-media-eid (:story-media/eid command-result)]
    (js/location.assign (str "/ui/social-schedule?story-eid="
                             story-media-eid))))

(defn download-callback!
  [{:keys [store]
    :as w}]
  (let [story-media-eid (:story-media/eid (:editor2 @store))
        dispatch! (:replicant/dispatch! @store)]
    (dispatch! {:actions [[:store/assoc :ui.editor2.timeline/share-menu-open false]
                          [:ui/modal-open :modal/download {:story-eid story-media-eid}]
                          [:story/download-poll]]})))

(defn track-promotion-click
  [{:keys [action]}]
  (matomo/fire-event! (second action)))

(defn add-ai-image
  [{:keys [action state]}]
  (let [params (second action)
        result (command/get-latest-result state (:command params))
        image-url (:image-generation/image-url result)
        height (get-in result [:image-generation/response
                               :height] (:default-height params))
        width (get-in result [:image-generation/response
                              :width] (:default-width params))
        asset-uuid (random-uuid)
        clip-uuid (random-uuid)
        slide (get-current-slide state)
        start (:slide/start slide 0)
        end (+ start
               (:slide/duration slide))
        dispatch! (:replicant/dispatch! state)
        asset {:asset/width width
               :asset/height height
               :asset/uuid asset-uuid
               :asset/type :image
               :asset/uri image-url
               :asset/content-type "image/jpeg"
               :asset/name "AI Image"}
        tx [asset
            {:clip/uuid clip-uuid
             :clip/asset [:asset/uuid asset-uuid]
             :clip/name "AI Image"
             :clip/offset 0
             :clip/start start
             :clip/end end}]]

    (dispatch! {:actions [[:upload-manager/add-upload
                           {:id asset-uuid
                            :blob-uri image-url
                            :callback-action [:editor2/add-asset-uploaded-uri
                                              asset-uuid]}]
                          [:editor2/prepare-assets!]]})
    (-> state
        (transact tx)
        (add-track [nil
                    {:after-track :first
                     :clip/uuid clip-uuid}]
                   (random-uuid)))))

(defn replace-ai-asset
  "Points all clips of the replaced asset to the newly generated one
   (regenerate workflow). The old asset stays available in the media
   sidebar. Visual clips are fitted and centered again since the new
   asset can have different dimensions; clips of assets with a duration
   get their range recomputed."
  [{:keys [state action]}]
  (let [{:keys [old-asset-uuid new-asset-uuid]} (second action)
        db (get-in state [:editor2 :db])
        ;; Joins via the uuid, see handle-legacy-filter-asset: the old
        ;; asset can be deleted while the generation is still running.
        clip-uuids (ds/q '[:find [?clip-uuid ...]
                           :in $ ?asset-uuid
                           :where
                           [?asset :asset/uuid ?asset-uuid]
                           [?clip :clip/asset ?asset]
                           [?clip :clip/uuid ?clip-uuid]]
                         db
                         old-asset-uuid)
        new-asset (ds/entity db [:asset/uuid new-asset-uuid])
        visual? (#{:image :video :gif} (:asset/type new-asset))]
    (reduce
      (fn [state clip-uuid]
        (let [clip (ds/entity (get-in state [:editor2 :db])
                              [:clip/uuid clip-uuid])
              slide (or (get-current-slide state)
                        (get-nearest-slide state))
              clip-range (when (and slide
                                    (:asset/duration new-asset))
                           (default-asset-clip-range new-asset
                                                     slide
                                                     (:clip/start clip)))
              state (transact state
                              [(cond-> {:clip/uuid clip-uuid
                                        :clip/asset [:asset/uuid new-asset-uuid]
                                        :clip/name (:asset/name new-asset)}
                                 clip-range (assoc :clip/start (:start clip-range)
                                                   :clip/end (:end clip-range)))])]
          (if visual?
            (-> state
                (fit-clip clip-uuid)
                (center-clip clip-uuid))
            state)))
      state
      clip-uuids)))

(defn ai-insert-action
  "Either adds the generated asset as a new clip, or, in the regenerate
   workflow, replaces the old asset in all its clips."
  [params asset-uuid]
  (if-let [old-asset-uuid (:replace-asset-uuid params)]
    [:editor2/replace-ai-asset {:old-asset-uuid old-asset-uuid
                                :new-asset-uuid asset-uuid}]
    [:editor2/add-asset-as-clip asset-uuid]))

(defn add-openrouter-image
  "Adds a generated OpenRouter image as an asset and clip. Unlike the
   legacy `add-ai-image`, the asset gets the actual pixel dimensions of
   the generated image (models return varying sizes) and the clip is
   placed via `add-asset-as-clip`, which fits and centers it on the
   canvas."
  [{:keys [store action]}]
  (let [params (second action)
        result (command/get-latest-result @store (:command params))
        image-url (:image-generation/image-url result)
        dispatch! (:replicant/dispatch! @store)]
    (when image-url
      (let [asset-uuid (random-uuid)
            asset (cond-> {:asset/uuid asset-uuid
                           :asset/type :image
                           :asset/uri image-url
                           :asset/content-type (:image-generation/content-type result
                                                                               "image/png")
                           :asset/width (:image-generation/width result
                                                                 (:default-width params))
                           :asset/height (:image-generation/height result
                                                                   (:default-height params))
                           :asset/name "AI Image"}
                    (:ai-data params) (assoc :asset/data (:ai-data params)))]
        (dispatch! {:actions [[:editor2/transact-asset [asset]]
                              [:upload-manager/add-upload
                               {:id asset-uuid
                                :blob-uri image-url
                                :callback-action [:editor2/add-asset-uploaded-uri
                                                  asset-uuid]}]
                              (ai-insert-action params asset-uuid)]})))))

(defn audio-duration-from-url
  "Loads only the metadata of the audio at `url` to determine its
   duration in milliseconds."
  [url]
  (let [audio (.createElement js/document "audio")]
    (set! (.-src audio) url)
    (js/Promise.
      (fn [resolve reject]
        (set! (.-onloadedmetadata audio)
              (fn []
                (resolve (* 1000 (.-duration audio)))))
        (set! (.-onerror audio)
              (fn [_e]
                (reject (js/Error. "The generated audio could not be loaded."))))))))

(defn add-ai-audio
  "Adds a generated AI voice (e.g. an OpenRouter text-to-speech result)
   as an audio asset and clip."
  [{:keys [store action]}]
  (let [params (second action)
        result (command/get-latest-result @store (:command params))
        audio-url (:speech-generation/audio-url result)
        content-type (:speech-generation/content-type result "audio/mpeg")
        ;; The extension matters: the Howler player derives the audio
        ;; format from the extension of the asset name (see
        ;; ui.editor2.audio-player).
        extension (if (= content-type "audio/wav")
                    ".wav"
                    ".mp3")
        label (if (= :music (get-in params [:ai-data :ai/category]))
                "AI Music"
                "AI Voice")
        dispatch! (:replicant/dispatch! @store)]
    (when audio-url
      (.then (audio-duration-from-url audio-url)
             (fn [duration-ms]
               (let [asset-uuid (random-uuid)
                     asset (cond-> {:asset/uuid asset-uuid
                                    :asset/type :audio
                                    :asset/uri audio-url
                                    :asset/content-type content-type
                                    :asset/name (str label " " asset-uuid extension)
                                    :asset/duration duration-ms}
                             (:ai-data params) (assoc :asset/data (:ai-data params)))]
                 (dispatch! {:actions [[:editor2/transact-asset [asset]]
                                       [:upload-manager/add-upload
                                        {:id asset-uuid
                                         :blob-uri audio-url
                                         :callback-action [:editor2/add-asset-uploaded-uri
                                                           asset-uuid]}]
                                       (ai-insert-action params asset-uuid)]})))))))

(defn add-ai-video
  "Adds a generated AI video (e.g. an OpenRouter video generation
   result) as a video asset and clip."
  [{:keys [store action]}]
  (go
    (let [{:keys [video-url ai-data] :as params} (second action)
          dispatch! (:replicant/dispatch! @store)
          asset* (<! (asset-from-video-url video-url))
          ;; `asset-from-video-url` provides :asset/duration-ms, but the
          ;; clip placement reads :asset/duration:
          asset (cond-> (assoc asset*
                               :asset/duration (:asset/duration-ms asset*)
                               :asset/name "AI Video")
                  ai-data (assoc :asset/data ai-data))]
      (dispatch! {:actions [[:editor2/transact-asset [asset]]
                            [:upload-manager/add-upload
                             {:id (:asset/uuid asset)
                              :blob-uri video-url
                              :callback-action [:editor2/add-asset-uploaded-uri
                                                (:asset/uuid asset)]}]
                            (ai-insert-action params (:asset/uuid asset))
                            [:editor2/prepare-assets!]]}))))

(defn set-slide-duration
  [{:keys [action state]}]
  (let [[_ slide-uuid duration] action]
    (if (and slide-uuid duration)
      (let [db (get-in state [:editor2 :db])
            slide (ds/entity db
                             [:slide/uuid slide-uuid])
            duration* (clamp {:value duration
                              :minimum (:slide/min-duration defaults/defaults)
                              :maximum (:slide/max-duration defaults/defaults)})
            tx [{:slide/uuid slide-uuid
                 :slide/duration duration*}]]
        (-> state
            (update-in [:editor2 :playhead-position]
                       (fn [playhead-position]
                         (min playhead-position
                              (+ (or (:slide/start slide) 0)
                                 duration*
                                 -10))))
            (transact tx)))
      state)))

(defn toggle-overlay
  [state]
  (update-in state [:editor2 :hide-instagram-overlay?] not))

(defn toggle-snapping
  [state]
  (set! (.-snappable ^js @composition/moveable-instance)
        (not (.-snappable ^js @composition/moveable-instance)))
  (.updateRect @composition/moveable-instance))

(defn exit-editor2
  [{:keys [action store]}]
  (prn "leave Editor2")
  (let [state @store
        unsaved-changes? (second action)
        dispatch! (:replicant/dispatch! state)]
    (if unsaved-changes?
      (.back js/window.history)
      (dispatch! {:actions [[:ui/modal-open :modal/exit {}]]}))))

(defn media-file?
  [file]
  (let [t (.-type file)]
    (or (.startsWith t "image/")
        (.startsWith t "audio/")
        (.startsWith t "video/"))))

(defn file-list->vec
  [files]
  (vec
    (for [idx (range (.-length files))]
      (aget files idx))))

(defn drop-media-handler
  [{:keys [store] :as params}]
  (let [event (:replicant/js-event params)
        files (file-list->vec (aget event "dataTransfer" "files"))
        supported-files (filterv media-file? files)
        dispatch! (:replicant/dispatch! @store)]
    (cond
      (seq supported-files)
      (dispatch! {:actions (cond-> [[:editor2/add-upload supported-files]]
                             (< (count supported-files)
                                (count files))
                             (conj [:js/alert "File not supported"]))})

      (seq files)
      (dispatch! {:actions [[:js/alert "File not supported"]]})

      :else
      nil)))

(defn q-all-clip-uuids
  [db]
  (ds/q '[:find [?uuid ...]
          :where
          [_ :clip/uuid ?uuid]]
        db))

(defn q-all-slide-uuids
  [db]
  (ds/q '[:find [?uuid ...]
          :where
          [_ :slide/uuid ?uuid]]
        db))

(defn isolate-clip-tx
  "Retracts all clips except `clip`.Sets :slide/background-color to
   transparent so that a png screenshot of the clip is also got proper
   transparent background."
  [db clip]
  (let [all-slide-uuids (q-all-slide-uuids db)
        all-clip-uuids (q-all-clip-uuids db)]
    (concat
      ;; Isolate the clip by retracting all other clips in the db:
      (keep
        (fn [clip-uuid]
          (when-not (= clip-uuid (:clip/uuid clip))
            [:db/retractEntity [:clip/uuid clip-uuid]]))
        all-clip-uuids)
      ;; Set all slide-backgrounds to transparent:
      (mapv
        (fn [slide-uuid]
          [:db/add [:slide/uuid slide-uuid]
           :slide/background-color "transparent"])
        all-slide-uuids))))

(defn load-design!
  "Intended for `ui.editor2.recorder`:
   Loads all fonts and the design blob. Then saves the design
   db into (:db* store) by fetching the design blob."
  [{:keys [store action]}]
  (let [params (second action)
        dispatch! (:replicant/dispatch! @store)]
    (-> (sticker/load-all-fonts)
        (.then (fn []
                 (js/fetch (:design-blob/url params))))
        (.then (fn [response]
                 (if (.-ok response)
                   (.text response)
                   (js/Promise.reject
                     (js/Error. (str "Fetch failed: " (.-status response)))))))
        (.then (fn [text]
                 (let [db (:db (edn/read-string text))]
                   (dispatch! {:actions [[:store/assoc :editor2
                                          (merge defaults/defaults
                                                 {:db* db})]]})
                   (set! (.-ready js/window) true))))
        (.catch (fn [error]
                  (js/console.error "load-design! failed:" error)
                  (set! (.-ready js/window) false)
                  (js/Promise.reject error))))))

(defn load-clip!
  "Intended for `ui.editor2.recorder`:
   Loads one clip into the editor view, prepares the attached asset and sets
   the playhead-position to `(:clip/start clip)` so that it actually
   shows up in the editor."
  [{:keys [store action]}]
  (let [params (second action)
        dispatch! (:replicant/dispatch! @store)
        db* (get-in @store [:editor2 :db*])
        clip (ds/entity db*
                        [:clip/uuid (:clip/uuid params)])]
    (if clip
      (let [tx (isolate-clip-tx db*
                                clip)
            db (common/transact-db db* tx)]
        (dispatch! {:actions [[:store/assoc-in [:editor2 :playhead-position] (:clip/start clip)]
                              [:store/assoc-in [:editor2 :db] db]]})
        (-> (prepare-assets! {:store store})
            (.then (fn []
                     (swap! store
                            assoc-in
                            [:editor2 :screenshot-ready]
                            true)
                     (set! (.-ready js/window) true)))
            (.catch (fn [error]
                      (js/console.error "load-clip! prepare-assets failed:" error)
                      (set! (.-ready js/window) false)))))
      (do
        (js/console.error "Clip not found:" (:clip/uuid params))
        (set! (.-ready js/window) false)))))

(def handlers
  {:editor2/drag
   drag-start-handler
   :editor2/clip-move-to-track
   (wrap-params-swap clip-move-to-track)
   :editor2/move-clip-layer-up
   (wrap-swap move-clip-layer-up)
   :editor2/move-clip-layer-down
   (wrap-swap move-clip-layer-down)
   :editor2/cycle-overlapping-clips
   (wrap-params-swap (fn [{:keys [state action]}]
                       (cycle-overlapping-clips state (second action))))
   :editor2/new-track
   (wrap-params-swap new-track)
   :editor2/delete-clip
   (wrap-swap delete-clip)
   :editor2/cut-clip
   (wrap-swap cut-clip)
   :editor2/timeline-zoom-out
   (wrap-swap zoom-out)
   :editor2/timeline-zoom-in
   (wrap-swap zoom-in)
   :editor2/transform-clip
   (wrap-params-swap transform-clip)
   :editor2/select-clip
   select-clip!
   :editor2/click-clip
   click-clip!
   :editor2/select-multiple-clips
   select-multiple-clips!
   :editor2/open-sticker-edit-modal
   open-sticker-edit-modal!
   :editor2/add-asset-as-clip
   add-asset-as-clip!
   :editor2/add-sticker-asset-as-clip
   add-sticker-asset-as-clip!
   :editor2/add-music-sticker
   add-music-sticker!
   :editor2/add-music-audio-clip
   add-music-audio-clip!
   :editor2/ensure-audio-players
   ensure-audio-players!
   :editor2/add-or-replace-giphy-asset
   add-or-replace-giphy-asset!
   :editor2/add-upload
   add-upload!
   :editor2/play
   play!
   :editor2/pause
   pause!
   :editor2/stop
   stop!
   :editor2/click-time-selector
   (wrap-params-swap click-time-selector)
   :editor2/prepare-assets!
   prepare-assets!
   :editor2/composition-zoom-out
   (wrap-swap composition-zoom-out)
   :editor2/composition-zoom-in
   (wrap-swap composition-zoom-in)
   :editor2/export-design
   export-design!
   :editor2/transact-asset
   (wrap-params-swap transact-asset)
   :editor2/set-asset-design
   (wrap-params-swap set-asset-design)
   :editor2/set-asset-invisible-design
   (wrap-params-swap set-asset-invisible-design)
   :editor2/set-asset-display-type
   (wrap-params-swap set-asset-display-type)
   :editor2/toggle-mute
   (wrap-params-swap (fn [{:keys [state action]}]
                       (toggle-mute state (second action))))
   :editor2/adapt-clip-data
   (wrap-params-swap adapt-clip-data)
   :editor2/set-background-color
   (wrap-params-swap set-background-color)
   :editor2/editor1-import
   editor1-import!

   ;; Text actions:
   :editor2/add-text-preset-as-clip!
   add-text-preset-as-clip!
   :editor2/update-text-asset!
   update-text-asset!
   :editor2/resize-text-clip!
   resize-text-clip!
   :editor2/toggle-text-mark!
   toggle-text-mark!
   :editor2/set-text-align!
   set-text-align!
   :editor2/set-text-line-height!
   set-text-line-height!
   :editor2/set-text-mark!
   set-text-mark!
   :editor2/set-text-mark-or-click!
   set-text-mark-or-click!
   :editor2/add-asset-uploaded-uri
   (wrap-params-swap add-asset-uploaded-uri)
   :editor2/handle-legacy-filter-asset
   (wrap-params-swap handle-legacy-filter-asset)
   :editor2/drop-legacy-filter-asset
   (wrap-params-swap drop-legacy-filter-asset)
   :editor2/retract-entity
   (wrap-params-swap retract-entity)
   :upload-manager/add-upload
   upload-manager/add-upload!
   :editor2/saved
   (wrap-params-swap saved)
   :editor2/load
   load!
   :editor2/register-hotkeys
   register-hotkeys
   :editor2/deselect-clips
   deselect-clips
   :editor2/add-ai-image
   (wrap-params-swap add-ai-image)
   :editor2/add-openrouter-image
   add-openrouter-image
   :editor2/replace-ai-asset
   (wrap-params-swap replace-ai-asset)
   :editor2/add-ai-audio
   add-ai-audio
   :editor2/add-ai-video
   add-ai-video
   :editor2/save!
   save!
   :editor2/exit
   exit-editor2
   :editor2/schedule-callback!
   schedule-callback!
   :editor2/download-callback!
   download-callback!
   :editor2/track-promotion-click
   track-promotion-click
   :editor2/set-slide-duration!
   (wrap-params-swap set-slide-duration)
   :editor2/drop-media
   drop-media-handler
   :editor2/toggle-ig-overlay
   (wrap-swap toggle-overlay)
   :editor2/toggle-snapping
   toggle-snapping
   :editor2/fetch-canva-status
   fetch-canva-status
   :editor2/canva-connect-success
   canva-connect-success
   :editor2/canva-connect-click
   canva-connect-click
   :editor2/canva-edit-design-success
   canva-edit-design-success
   :editor2/canva-import-design-success
   canva-import-design-success
   :editor2/canva-import-click
   canva-import-click
   :editor2/canva-sidebar-import-design
   canva-sidebar-import-design
   :editor2/canva-sidebar-import-design-success
   canva-sidebar-import-design-success
   :editor2/canva-cancel-page-selection
   canva-cancel-page-selection
   :editor2/canva-toggle-page
   canva-toggle-page
   :editor2/canva-select-all-pages
   canva-select-all-pages
   :editor2/canva-confirm-page-selection
   canva-confirm-page-selection
   :editor2/canva-button-click
   canva-button-click
   :editor2/canva-open-edit-url
   (fn [{:keys [action]}]
     (when-let [url (second action)]
       (.open js/window url "_blank")))
   :editor2-recorder/load-clip
   load-clip!
   :editor2-recorder/load-design
   load-design!
   })

(def registry-entries
  (map
    (fn [[kind handler]]
      {:ui.action/kind kind
       :ui.action/handler handler})
    handlers))
